import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  FiLayers,
  FiCheck,
  FiCheckSquare,
  FiChevronDown,
  FiChevronRight,
  FiCornerUpRight,
  FiDownload,
  FiEdit2,
  FiEye,
  FiFileText,
  FiFolder,
  FiFolderPlus,
  FiImage,
  FiLogOut,
  FiMessageSquare,
  FiMoreHorizontal,
  FiPlusSquare,
  FiSliders,
  FiTrash2,
  FiUpload,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import { FaFilePdf, FaPlay } from 'react-icons/fa';
import { FaFileExcel, FaFileWord } from 'react-icons/fa6';
import { BsFiletypeCsv, BsFiletypeMd, BsFiletypeTxt } from 'react-icons/bs';
import { TbFileTypePpt, TbMailFilled } from 'react-icons/tb';
import { IoIosSave } from 'react-icons/io';
import {
  createProjectConversationFolder,
  createProjectFileFolder,
  deleteConversation,
  deleteProjectConversationFolder,
  deleteProjectFile,
  deleteProjectFileFolder,
  getProject,
  getProjectFilePreview,
  getProjectFileThumbnailUrl,
  listConversations,
  listProjectFiles,
  listProjectGroupChats,
  listHeldFiles,
  answerHeldFiles,
  listProjectMembers,
  markProjectFilesSeen,
  markProjectSeen,
  renameProjectConversationFolder,
  renameProjectFile,
  renameProjectFileFolder,
  reorderProjectConversations,
  setConversationKnowledgeScope,
  setProjectConversationFolder,
  setProjectFileFolder,
  updateConversationName,
  updateProject,
  uploadProjectFile,
} from '../utils/conversationApi';
import {
  fetchProjectFiles,
  getCachedProjectFiles,
  hasCachedProjectFiles,
  setCachedProjectFiles,
} from '../utils/projectFilesCache';
import { useAuth } from '../auth/AuthContext';
import { useLiveRefresh } from '../hooks/useLiveRefresh';
import ProjectMembersModal from './ProjectMembersModal';
import LeaveProjectModal from './LeaveProjectModal';
import FocusModeTag from './FocusModeTag';
import { GroupChatPromoteModal, GroupChatsSection, projectIsShared } from './GroupChat';
import { focusModeTagMatches } from '../utils/focusModes';
import './ProjectPanel.css';

// Fired whenever a project's default_rule_ids are saved. ChatInterface.js
// caches project objects (projectsById) keyed by id, fetched once per id
// rather than on every render -- without this, a conversation already open
// in that project would keep showing its OLD rule pills until the user
// switched conversations and back.
export const PROJECT_UPDATED_EVENT = 'kai-project-updated';

// Fired whenever this panel successfully changes the ACTIVE conversation's
// knowledge_scope (the Files tab checkboxes below). ChatInterface.js is a
// SIBLING of this panel under ProjectView, not a descendant, so it can't be
// handed a callback prop the way handleSaveInstructions's onProjectUpdated
// is -- this mirrors PROJECT_UPDATED_EVENT's own cross-sibling pattern instead.
// detail: {conversation_id, knowledge_scope, note}. See
// .claude/plan/plan_knowledge_scope.md.
export const KNOWLEDGE_SCOPE_UPDATED_EVENT = 'kai-knowledge-scope-updated';

// Fired whenever a file is added to a project's library from OUTSIDE this
// panel's own Knowledge base tab -- currently only ProjectSetupModal, whose
// upload zone calls uploadProjectFile directly rather than going through an
// already-mounted ProjectPanel instance. Without this, a panel already open
// on this project keeps showing its stale (pre-upload) file list until
// something else happens to remount it. detail: {project_id}.
export const PROJECT_FILES_UPDATED_EVENT = 'kai-project-files-updated';

// How often an open project re-reads what other people may have changed in
// it (members, files, conversations, its own settings) -- see the live
// refresh near loadHistory below.
const LIVE_REFRESH_MS = 30000;
// The project fields someone else can change while it's open here: a rename,
// its instructions or folders, your access level, who's in it, and whether
// there's something new in its group chats (the Conversations tab's dot).
const LIVE_PROJECT_FIELDS = [
  'name', 'instructions', 'default_rule_ids', 'file_folders', 'conversation_folders',
  'my_role', 'shared_with', 'pending_invites', 'ownerless', 'group_unseen',
];
const liveFieldsOf = (project) => JSON.stringify(LIVE_PROJECT_FIELDS.map((key) => project?.[key]));

// Fired with a conversation's latest list of held files -- project files
// someone else added after it joined the project, left out of it until the
// user adds them (see the backend's knowledge_hold_for). ChatInterface's
// "Use in this chat" notice and this panel's Knowledge base tab both fetch
// it and both answer it, so each broadcasts what it got and the other just
// takes it (never refetches on it). detail: {conversation_id, held_files, note?}.
export const KNOWLEDGE_HOLD_UPDATED_EVENT = 'kai-knowledge-hold-updated';

// Id source for the `uploads` list below -- module-level, like
// ProjectSetupModal.js's own _fileRowSeq, since it only needs to be
// unique within whatever list is currently on screen, not per-instance.
let uploadIdSeq = 0;

// Must match .project-upload-spinner's own `animation: spin Ns` duration in
// ProjectPanel.css. Each spinner starts its CSS animation the moment ITS
// row mounts, so two uploads that started even a fraction of a second apart
// spin visibly out of phase with each other by default. Every spinner below
// gets a negative animation-delay of "how far into the current cycle the
// shared wall clock already is" (computed once, at upload-start, from
// Date.now()) instead -- since every spinner reads the same clock modulo
// the same duration, they all land on the same phase and visually rotate
// in lockstep, regardless of when each one actually mounted.
const UPLOAD_SPINNER_DURATION_MS = 800;
const spinnerSyncDelayMs = () => -(Date.now() % UPLOAD_SPINNER_DURATION_MS);

// No size check here: the server holds a knowledge-base document of every
// format below (PDF, Word, Excel, PowerPoint, text, email) only to S3's own
// 5GB limit (DOCUMENT_UPLOAD_MAX_BYTES), and reads each one from disk, not
// memory. Images stay at 25MB and recordings at 2GB.

// Must match PDF_INDEX_GENERIC_EXTENSIONS in backend/config.py plus .pdf
// itself -- every format the project library can actually index.
const INDEXABLE_EXTENSIONS = ['.pdf', '.txt', '.md', '.markdown', '.csv', '.docx', '.xlsx', '.pptx', '.eml', '.msg'];

// Must match IMAGE_LIBRARY_EXTENSIONS in backend/config.py -- a raw image
// stored (resized) as its own one-image "document", retrievable later via
// get_pdf_image the same way a PDF's own embedded images are. Kept as a
// separate list from INDEXABLE_EXTENSIONS above (rather than merged into it)
// since "indexed" specifically means "has text a vector search can find" --
// an image never does, so the word would be misleading applied to it.
const IMAGE_LIBRARY_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.bmp', '.tiff', '.tif'];

// Must match TRANSCRIBABLE_MEDIA_EXTENSIONS in backend/config.py. Uploading
// one here goes through the exact same /upload-file -> AWS Transcribe job as
// a chat attachment (see uploadProjectFile in conversationApi.js) -- the only
// difference is project_id is set from the start, so the finished transcript
// is filed into this project's library instead of staying conversation-only.
export const TRANSCRIBABLE_MEDIA_EXTENSIONS = ['.mp4', '.mp3', '.m4a'];

// Exported so ProjectSetupModal's own upload zone (the "add files" step
// shown right after creating a project) validates against the exact same
// list this panel's own Knowledge base tab does, rather than a second,
// driftable copy.
export const ALL_UPLOAD_EXTENSIONS = [...INDEXABLE_EXTENSIONS, ...IMAGE_LIBRARY_EXTENSIONS, ...TRANSCRIBABLE_MEDIA_EXTENSIONS];

const IMAGE_LIBRARY_EXTENSIONS_SET = new Set(IMAGE_LIBRARY_EXTENSIONS);

const getFileExtension = (name) => {
  const idx = (name || '').lastIndexOf('.');
  return idx === -1 ? '' : name.slice(idx).toLowerCase();
};

// One glance at the row should say what kind of file this is without
// reading the name or scrolling to see its extension -- a plain FiFileText
// for every row (the old behavior) made every format look the same. Colors
// loosely match each format's own real-world branding (red PDF, blue Word,
// green Excel, orange PowerPoint) since that's the association users already
// have from every other app that shows these. Word/Excel/csv use fa6's
// FaFileWord/FaFileExcel/BsFiletypeCsv (a solid document-with-glyph glyph)
// rather than fa5's outline-only equivalents, which read as dated.
const FILE_TYPE_ICONS = {
  '.pdf': [FaFilePdf, 'project-file-icon-pdf'],
  '.docx': [FaFileWord, 'project-file-icon-word'],
  '.xlsx': [FaFileExcel, 'project-file-icon-excel'],
  '.csv': [BsFiletypeCsv, 'project-file-icon-excel'],
  '.pptx': [TbFileTypePpt, 'project-file-icon-powerpoint'],
  '.eml': [TbMailFilled, 'project-file-icon-email'],
  '.msg': [TbMailFilled, 'project-file-icon-email'],
  '.txt': [BsFiletypeTxt, 'project-file-icon-text'],
  '.md': [BsFiletypeMd, 'project-file-icon-text'],
  '.markdown': [BsFiletypeMd, 'project-file-icon-text'],
};

// mp3/mp4/m4a get a colorful gradient "play" badge instead of a flat file
// icon, matching the reference images the user supplied -- a squircle badge
// for video (mp4, and m4a grouped with it), a round one for audio-only mp3.
const MEDIA_BADGE_EXTENSIONS = {
  '.mp4': 'video',
  '.m4a': 'video',
  '.mp3': 'audio',
};

// The small format icon beside a file's name in the confirm dialog's name box.
const ModalFileIcon = ({ name }) => {
  const ext = getFileExtension(name);
  const [Icon, colorClass] = IMAGE_LIBRARY_EXTENSIONS_SET.has(ext)
    ? [FiImage, 'project-file-icon-text']
    : FILE_TYPE_ICONS[ext] || [FiFileText, 'project-file-icon-text'];
  return <Icon className={`project-panel-modal-subject-icon ${colorClass}`} />;
};

const MediaBadge = ({ variant }) => (
  <span className={`project-file-media-badge project-file-media-badge-${variant}`}>
    <FaPlay className="project-file-media-badge-play" />
  </span>
);

// The Knowledge base row's leading icon/thumbnail. Images render an actual
// preview of themselves (fetched lazily below) rather than a generic icon --
// everything else gets a format-specific icon so the file type reads at a
// glance. Keeps the "no text, has images" case (a scanned/image-only PDF)
// as FiImage, same as before this change.
const FileTypeGlyph = ({ file, projectId }) => {
  const ext = getFileExtension(file.name);
  if (IMAGE_LIBRARY_EXTENSIONS_SET.has(ext)) {
    return <FileThumbnail file={file} projectId={projectId} />;
  }
  if (MEDIA_BADGE_EXTENSIONS[ext]) {
    return <MediaBadge variant={MEDIA_BADGE_EXTENSIONS[ext]} />;
  }
  if (!file.chunk_count && file.image_count) {
    return <FiImage className="project-file-icon" />;
  }
  const mapped = FILE_TYPE_ICONS[ext];
  if (mapped) {
    const [Icon, className] = mapped;
    return <Icon className={`project-file-icon ${className}`} />;
  }
  return <FiFileText className="project-file-icon" />;
};

// A standalone image upload's own image (see pdf_index_service.
// store_standalone_image), fetched once per file_id and cached for the life
// of this row. Falls back to the plain FiImage icon while loading or if the
// file has no stored image yet (e.g. mid-upload).
const FileThumbnail = ({ file, projectId }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    getProjectFileThumbnailUrl(projectId, file.file_id).then((u) => {
      if (!cancelled) setUrl(u);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [projectId, file.file_id]);

  if (!url) return <FiImage className="project-file-icon" />;
  return (
    <img
      src={url}
      alt=""
      className="project-file-thumbnail"
      onError={() => setUrl(null)}
    />
  );
};

// Wraps a raw file URL in Microsoft's public embed viewer -- the only way
// to render a real docx/xlsx/pptx inline in a browser without bundling a
// document-rendering library of our own. Requires the URL to be reachable
// from the public internet, which a presigned S3 URL is.
const officeViewerUrl = (fileUrl) => `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(fileUrl)}`;

// Where the preview's "Open in new tab" goes: a page the browser can show,
// never one that downloads. Office files open in Microsoft's viewer (their
// own link would download); an email or a file with no preview has no such
// page, so it gets Download only.
const previewNewTabUrl = (preview) => {
  if (preview.kind === 'office') return officeViewerUrl(preview.view_url);
  if (preview.kind === 'email' || preview.kind === 'none') return null;
  return preview.view_url;
};

// A CSV's first rows (see the server's _preview_table), its first row as the
// header -- a CSV almost always starts with one. Short rows are padded so
// every column lines up.
const PreviewTable = ({ rows, truncated }) => {
  if (!rows || rows.length === 0) {
    return <div className="project-file-preview-none">This file is empty.</div>;
  }
  const width = Math.max(...rows.map((row) => row.length));
  const pad = (row) => row.concat(Array(width - row.length).fill(''));
  const [header, ...body] = rows;
  return (
    <>
      {truncated && (
        <div className="project-file-preview-note">
          Showing the first {body.length.toLocaleString()} rows. Download the file to see all of it.
        </div>
      )}
      <div className="project-file-preview-table-wrap">
        <table className="project-file-preview-table">
          <thead>
            <tr>{pad(header).map((cell, i) => <th key={i} title={cell}>{cell}</th>)}</tr>
          </thead>
          <tbody>
            {body.map((row, r) => (
              <tr key={r}>{pad(row).map((cell, i) => <td key={i} title={cell}>{cell}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// The eye-icon preview modal's body -- how to render it depends entirely on
// `kind` (see get_file_preview_info's own docstring for what produces each
// one). 'pdf'/'text' both render as a plain <iframe>: the server serves the
// view link as a PDF or as plain text, which every browser shows inline.
// 'table' and 'email' arrive already read by the server, since a browser
// would download a CSV or an email instead of showing it.
const FilePreviewBody = ({ preview }) => {
  switch (preview.kind) {
    case 'pdf':
    case 'text':
      return <iframe src={preview.view_url} title={preview.name} className="project-file-preview-frame" />;
    case 'table':
      return <PreviewTable rows={preview.rows} truncated={preview.truncated} />;
    case 'email':
      return <pre className="project-file-preview-text">{preview.text}</pre>;
    case 'office':
      return (
        <iframe
          src={officeViewerUrl(preview.view_url)}
          title={preview.name}
          className="project-file-preview-frame"
        />
      );
    case 'image':
      return (
        <div className="project-file-preview-image-wrap">
          <img src={preview.view_url} alt={preview.name} className="project-file-preview-image" />
        </div>
      );
    case 'video':
      return (
        <div className="project-file-preview-media-wrap">
          {/* eslint-disable-next-line jsx-a11y/media-has-caption -- a transcribed recording's own captions aren't tracked as a separate WebVTT file anywhere in this pipeline. */}
          <video src={preview.view_url} controls autoPlay className="project-file-preview-video" />
        </div>
      );
    case 'audio':
      return (
        <div className="project-file-preview-media-wrap">
          {/* eslint-disable-next-line jsx-a11y/media-has-caption -- same as the video case above. */}
          <audio src={preview.view_url} controls autoPlay className="project-file-preview-audio" />
        </div>
      );
    default:
      return (
        <div className="project-file-preview-none">
          No inline preview available for this file. Use Download above to open it.
        </div>
      );
  }
};

const formatDate = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// A file's one-line status -- but only worth showing at all while there's
// something to actually say: mid-index, or failed. Once a file is done, the
// row just shows its name, same as everything else in this panel (a
// conversation row doesn't show a byte count either) -- the page/chunk/image
// counts and file size used to always render here ("358 chunks · 1.1MB ·
// indexed") and read as engineering internals to anyone who isn't the one
// who built the indexer. A truncated upload still gets its own dedicated
// warning banner right below the row (see project-panel-error), so nothing
// about that case is lost by dropping it from this line too.
const fileMetaLine = (file) => {
  if (file.status === 'IN_PROGRESS') return 'Indexing…';
  if (file.status !== 'COMPLETED') return 'Indexing failed';
  return null;
};

// What indexing couldn't keep from a file -- pictures it couldn't read,
// pictures too large to read, pages it had to skip -- so nothing is left
// out without the list saying so. null when nothing was.
export const leftOutNotice = (file) => {
  const plural = (n, one, many) => (n === 1 ? one : many);
  const parts = [];
  const failed = file.images_failed || 0;
  const tooLarge = file.images_too_large || 0;
  const skipped = file.pages_skipped || [];
  if (failed > 0) parts.push(`${failed} ${plural(failed, "image couldn't", "images couldn't")} be read`);
  if (tooLarge > 0) parts.push(`${tooLarge} ${plural(tooLarge, 'image was', 'images were')} too large to read`);
  if (skipped.length > 0) {
    const pages = skipped.slice(0, 10).map((p) => p.page).join(', ');
    const more = skipped.length > 10 ? ` and ${skipped.length - 10} more` : '';
    parts.push(`${plural(skipped.length, 'page', 'pages')} ${pages}${more} couldn't be read`);
  }
  if (parts.length === 0) return null;
  const sentence = parts.join('; ');
  return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}, so search can't find what's in them.`;
};

// Loading state reads as "this pane is filling in", not "the whole panel
// froze" -- a few flat, pulsing rows shaped like the real rows that are about
// to replace them, confined to whichever pane is actually loading.
function SkeletonRows({ count = 3, rowHeight = 48 }) {
  return (
    <div className="project-panel-skeleton" aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="project-panel-skeleton-row" style={{ height: rowHeight }} />
      ))}
    </div>
  );
}

// ── Per-conversation Knowledge base scope (Files tab checkboxes) ───────────
// See .claude/plan/plan_knowledge_scope.md. `scope` is null/undefined (no
// explicit selection -- every file is in scope, today's default) or
// {mode: 'all'|'selected', file_ids, folder_ids}. A folder's members are a
// LIVE reference (folder_id membership), never a snapshot -- these helpers
// only ever consult the CURRENT `files`/`folders` passed in, never a cached copy.

function isFileInScope(file, scope) {
  if (!scope || scope.mode !== 'selected') return true;
  if ((scope.file_ids || []).includes(file.file_id)) return true;
  return Boolean(file.folder_id) && (scope.folder_ids || []).includes(file.folder_id);
}

function isFolderInScope(folder, scope) {
  if (!scope || scope.mode !== 'selected') return true;
  return (scope.folder_ids || []).includes(folder.folder_id);
}

// The stored shape is purely ADDITIVE (mode 'selected' lists what's IN, never
// what's excluded), so unchecking one item out of an implicit/explicit "all"
// state has to first normalize into an explicit selection covering
// everything else. A sibling still fully covered by its own folder entry
// stays that way; only the excluded item's own folder (if any) has to be
// broken apart into its individual members.
function explicitScopeExcludingFile(files, folders, excludeFileId) {
  const excluded = files.find((f) => f.file_id === excludeFileId);
  const excludedFolderId = excluded?.folder_id || null;
  const folder_ids = folders.map((f) => f.folder_id).filter((id) => id !== excludedFolderId);
  const file_ids = files
    .filter((f) => f.file_id !== excludeFileId)
    .filter((f) => !f.folder_id || f.folder_id === excludedFolderId)
    .map((f) => f.file_id);
  return { mode: 'selected', file_ids, folder_ids };
}

function explicitScopeExcludingFolder(files, folders, excludeFolderId) {
  const folder_ids = folders.map((f) => f.folder_id).filter((id) => id !== excludeFolderId);
  const file_ids = files.filter((f) => !f.folder_id).map((f) => f.file_id);
  return { mode: 'selected', file_ids, folder_ids };
}

function toggleFileInScope(files, folders, scope, fileId) {
  const file = files.find((f) => f.file_id === fileId);
  if (!file) return scope;
  if (isFileInScope(file, scope)) {
    if (!scope || scope.mode !== 'selected') {
      return explicitScopeExcludingFile(files, folders, fileId);
    }
    if ((scope.file_ids || []).includes(fileId)) {
      return { ...scope, file_ids: scope.file_ids.filter((id) => id !== fileId) };
    }
    // Covered only via a whole-folder entry -- carve this one file out of it,
    // keeping every sibling covered by listing them individually instead.
    const folderId = file.folder_id;
    const siblingIds = files
      .filter((f) => f.folder_id === folderId && f.file_id !== fileId)
      .map((f) => f.file_id);
    return {
      mode: 'selected',
      file_ids: [...new Set([...(scope.file_ids || []), ...siblingIds])],
      folder_ids: (scope.folder_ids || []).filter((id) => id !== folderId),
    };
  }
  const base = scope && scope.mode === 'selected' ? scope : { mode: 'selected', file_ids: [], folder_ids: [] };
  return { ...base, file_ids: [...new Set([...(base.file_ids || []), fileId])] };
}

function toggleFolderInScope(files, folders, scope, folderId) {
  const folder = folders.find((f) => f.folder_id === folderId);
  if (!folder) return scope;
  if (isFolderInScope(folder, scope)) {
    if (!scope || scope.mode !== 'selected') {
      return explicitScopeExcludingFolder(files, folders, folderId);
    }
    return { ...scope, folder_ids: (scope.folder_ids || []).filter((id) => id !== folderId) };
  }
  const base = scope && scope.mode === 'selected' ? scope : { mode: 'selected', file_ids: [], folder_ids: [] };
  // Whole-folder coverage supersedes any of its own members individually
  // listed -- drop them so the stored scope carries no redundant entries.
  const memberIds = new Set(files.filter((f) => f.folder_id === folderId).map((f) => f.file_id));
  return {
    mode: 'selected',
    file_ids: (base.file_ids || []).filter((id) => !memberIds.has(id)),
    folder_ids: [...new Set([...(base.folder_ids || []), folderId])],
  };
}

// Two conversations that have BOTH been manually dragged into position (see
// handleConversationDrop) keep that explicit relative order, regardless of
// which one was touched more recently -- that's the whole point of manual
// reordering, and it must survive new activity in either one.
//
// But a conversation that has NEVER been reordered competes on recency
// (updated_at) against everything else, including already-ordered ones --
// it does NOT get shoved to the very end just for lacking a sort_order.
// Reordering assigns 0..n-1 to every conversation that existed in the pane
// at that moment (see set_project_conversation_order), so any conversation
// created AFTER the last reorder starts out with no sort_order at all; if
// "no sort_order" always sorted last, a project reordered once early on
// would permanently bury every conversation created afterward beneath that
// one old, stale arrangement -- which is exactly backwards from "by default,
// newest first; a manual reorder is an override, not a one-time ceiling."
function sortByManualOrder(conversations) {
  return [...conversations].sort((a, b) => {
    if (a.sort_order != null && b.sort_order != null) {
      if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
    }
    return new Date(b.updated_at) - new Date(a.updated_at);
  });
}

// Labels match the terms used everywhere else a project's three parts are
// described (the header description right below, ProjectView's own project
// page, and ChatInterface's "this project's instructions/knowledge base"
// copy) -- keys stay short/internal since they're just state values, never
// shown to the user. Order here is the visual tab order (TABS.map below),
// independent of each key's own default/active-tab handling elsewhere.
const TABS = [
  { key: 'files', label: 'Knowledge base', icon: FiFolder },
  { key: 'chats', label: 'Conversations', icon: FiMessageSquare },
  { key: 'rules', label: 'Instructions', icon: FiSliders },
];

// One global map, shared by every project: {project_id: last tab used}.
// Keyed by project rather than one flat value so switching between two
// projects each keeps its own place -- a user who leaves one project on
// Files and another on Rules shouldn't have opening either one clobber the
// other's tab.
const TAB_STORAGE_KEY = 'kai_project_panel_active_tab_v1';

function loadStoredTab(projectId) {
  if (!projectId) return null;
  try {
    const map = JSON.parse(localStorage.getItem(TAB_STORAGE_KEY)) || {};
    const tab = map[projectId];
    return TABS.some((t) => t.key === tab) ? tab : null;
  } catch (error) {
    return null;
  }
}

function storeTab(projectId, tab) {
  if (!projectId) return;
  try {
    const map = JSON.parse(localStorage.getItem(TAB_STORAGE_KEY)) || {};
    map[projectId] = tab;
    localStorage.setItem(TAB_STORAGE_KEY, JSON.stringify(map));
  } catch (error) {
    // Best-effort persistence -- a private-browsing quota error here must
    // never break switching tabs.
  }
}

// The project's panel opens on its Conversations tab next time -- for a
// conversation just added to the project, so it's in view there.
export function showProjectConversationsTab(projectId) {
  storeTab(projectId, 'chats');
}


/**
 * A project's shared context, as three tabs sharing one pane: a single
 * free-text instructions field that applies to every conversation in it
 * (Instructions), its indexed file library (Knowledge base), and the
 * conversations filed under it (Conversations). Exactly one is ever visible
 * -- see `activeTab` -- so the panel never has to split its height three
 * ways.
 *
 * Rendered in two places, distinguished by `variant`:
 * - "page": the right-hand column of ProjectView.js (the project's full-page
 *   home).
 * - "sidebar": an always-visible column next to the chat itself, for the
 *   whole time the active conversation belongs to a project.
 * Both get the same header -- a generic "what a project is" blurb, not the
 * project's own name (ProjectView.js's own page title already names it, and
 * a "sidebar" mount would have its own conversation title bar doing the
 * same), so this panel never duplicates a name that's already on screen
 * somewhere else.
 *
 * The Instructions tab is a plain textarea bound to `project.instructions`,
 * with its own explicit Save (see handleSaveInstructions) -- deliberately
 * NOT the instant-apply-on-toggle model a per-conversation custom rule uses,
 * since a big block of free text needs a moment to review before it goes
 * live for the whole project. A project's OLDER instructions mechanism
 * (picking from the user's own custom_rules library, via default_rule_ids)
 * still exists in the backend for compatibility but is no longer surfaced
 * here -- see config.py's own comment on MAX_PROJECT_DEFAULT_RULES.
 */
function ProjectPanel({
  project,
  variant = 'sidebar',
  activeConversationId,
  // The active conversation's own knowledge_scope, and (when there is no
  // active conversation -- the project's landing page, about to start one) a
  // pending scope + setter App.js owns instead, since ChatInterface also
  // needs to read it at the first message's send time. See
  // .claude/plan/plan_knowledge_scope.md.
  activeConversationKnowledgeScope,
  pendingKnowledgeScope,
  onPendingKnowledgeScopeChange,
  onOpenConversation,
  onStartConversationInProject,
  onProjectUpdated,
  // Move to Recycle Bin -- this panel's own "..." menu (see projectMenuOpen
  // below) only renders when this is passed in, so a caller that doesn't
  // wire it up (e.g. an embedded sidebar variant elsewhere) simply doesn't
  // get the button rather than getting a dead one. ProjectView.js passes
  // it; this used to be its own "..." button sitting next to the "Test" h1
  // title in the main column, moved here to sit next to "Personal
  // Projects"' own gear icon instead (see project-view-config-btn).
  // Rename lives entirely in this panel now (see handleStartRenameHeader
  // below) instead of being lifted up to a parent-owned callback -- it
  // used to open ProjectView's own h1 title as an editable field, which
  // put the edit box nowhere near the "..." menu the user had just clicked.
  onDeleteProject,
  // Shows a close button at the end of the header. Passed only by ProjectView
  // while this panel is a full-screen drawer on a phone/tablet, where it is the
  // one way to dismiss it (nothing of the page is left to tap outside).
  onClose,
  // Called with the project_id once it's gone from the CALLER's own
  // access -- either they deleted it (see onDeleteProject's confirm flow,
  // owned by ProjectView.js) or they just left one they didn't create (see
  // ProjectMembersModal's onLeft below). Either way the parent navigates
  // away the same way.
  onProjectDeleted,
  // Called after a conversation in this project is deleted, so the parent can
  // clear the chat pane if that's what it was showing.
  onConversationDeleted,
  refreshTrigger = 0,
  // Bumped each time a reply finishes (see App.js's handleMessageSent): the
  // conversation list is re-read quietly, so the row's time and focus mode
  // tag update without the loading state refreshTrigger brings.
  conversationActivityTrigger = 0,
  // Bumped by ProjectView only when a NEW conversation is actually started
  // (not on every refreshTrigger cause) -- see the effect below, which jumps
  // this panel to the Knowledge base tab right when it changes so the user
  // can update/narrow the knowledge base for that conversation before
  // sending a first message, rather than digging for it afterward.
  newConversationTrigger = 0,
}) {
  // View can read the knowledge base and instructions, select files for its
  // own conversation, and chat -- but not add/edit/delete anything. Owner
  // (the creator) and Edit both get full content control. See
  // .claude/plan/projectsharing.md's "Permission model". Defaults to true
  // when my_role is absent (e.g. a project object built before this field
  // existed) so this never regresses an existing solo project's own owner.
  const canEdit = project?.my_role ? ['edit', 'owner'].includes(project.my_role) : true;
  // Creator-only: renaming and deleting stay with whoever created it.
  // Inviting, changing someone's access and removing people follow canEdit
  // above (Edit members can do all three) -- see ProjectMembersModal.
  const isCreator = project?.my_role ? project.my_role === 'owner' : true;
  // Only to say "Added by you" rather than your own name -- see uploaderName.
  const { user } = useAuth();

  // This panel's own header "..." menu (Rename / Move to Recycle Bin) --
  // see onRenameProject/onDeleteProject above. Local state, since the menu's
  // open/closed-ness has no meaning outside this panel; only the two actions
  // themselves are lifted up to the parent that actually owns rename/delete.
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);
  const projectMenuRef = useRef(null);
  // "Manage members" / "See who has access" -- see the menu item below.
  const [membersModalOpen, setMembersModalOpen] = useState(false);
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const [members, setMembers] = useState([]);
  // A member's name for the header faces' tooltips, "(you)" on your own.
  const memberName = (m) => (
    `${m.display_name || m.email || 'Unknown member'}${user && m.user_id === user.id ? ' (you)' : ''}`
  );

  useEffect(() => {
    if (!projectMenuOpen) return undefined;
    const handleClickOutside = (e) => {
      if (projectMenuRef.current && !projectMenuRef.current.contains(e.target)) {
        setProjectMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [projectMenuOpen]);

  // Just for the header's "this project is shared" avatar stack -- fetched
  // independently of the members modal so it renders whether or not that
  // modal has ever been opened. See .claude/plan/projectsharing.md.
  // `quiet` (the live refresh, or the members modal closing): a failure keeps
  // the faces already showing.
  const loadMembers = useCallback(async ({ quiet = false } = {}) => {
    const requestedFor = project?.project_id;
    if (!requestedFor) return;
    try {
      const list = await listProjectMembers(requestedFor);
      if (projectIdRef.current !== requestedFor) return;
      // Only people who have accepted: an invitee who hasn't answered yet
      // isn't "in" the project, so no avatar for them (the members modal
      // still lists them, marked Pending, for the owner to manage).
      const joined = list.filter((m) => !m.pending);
      setMembers((prev) => (JSON.stringify(prev) === JSON.stringify(joined) ? prev : joined));
    } catch {
      if (!quiet && projectIdRef.current === requestedFor) setMembers([]);
    }
  }, [project?.project_id]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  // Clears the unread badge the moment the project is actually opened, per
  // the user's own spec -- not just seen in the Projects dropdown list.
  // The server flag alone isn't enough: App.js caches the project list (and
  // derives the Projects icon badge from it), so once the server has accepted
  // it we hand the cached copy back with unseen cleared. A failure leaves the
  // badge showing, which self-corrects next time this succeeds.
  const latestRef = useRef({ project, onProjectUpdated });
  latestRef.current = { project, onProjectUpdated };
  useEffect(() => {
    if (!(project?.shared && project?.unseen && project?.project_id)) return;
    const seenProjectId = project.project_id;
    markProjectSeen(seenProjectId)
      .then(() => {
        const { project: current, onProjectUpdated: notify } = latestRef.current;
        if (current?.project_id === seenProjectId) notify?.({ ...current, unseen: false });
      })
      .catch(() => {});
  }, [project?.project_id, project?.shared, project?.unseen]);

  // App.js's copy of a project is from when it loaded the project list, and
  // other people keep changing it: renaming it, editing its instructions or
  // folders, changing your View/Edit level, inviting or removing people.
  // This re-reads it and hands back whatever of LIVE_PROJECT_FIELDS differs,
  // the same way a change made here is passed on. A shared project re-checks
  // each time it's opened (your access level may have changed); the live
  // refresh below runs this again while it stays open.
  const refreshProject = useCallback(async () => {
    const checkedProjectId = project?.project_id;
    if (!checkedProjectId) return;
    const fieldsBefore = liveFieldsOf(latestRef.current.project);
    try {
      const fresh = await getProject(checkedProjectId);
      const { project: current, onProjectUpdated: notify } = latestRef.current;
      if (current?.project_id !== checkedProjectId) return;
      // Changed here while this was on its way: this copy may predate that.
      if (liveFieldsOf(current) !== fieldsBefore) return;
      const changed = LIVE_PROJECT_FIELDS.filter(
        (key) => fresh?.[key] !== undefined && JSON.stringify(fresh[key]) !== JSON.stringify(current[key])
      );
      if (!changed.length) return;
      const updated = { ...current };
      changed.forEach((key) => { updated[key] = fresh[key]; });
      notify?.(updated);
      window.dispatchEvent(new CustomEvent(PROJECT_UPDATED_EVENT, { detail: updated }));
    } catch {
      // Best-effort: the next refresh tries again.
    }
  }, [project?.project_id]);

  useEffect(() => {
    if (project?.shared) refreshProject();
  }, [project?.shared, refreshProject]);

  // Renaming the project inline, right in this header -- see the "Rename"
  // menu item below. Only the name itself is ever editable; the "Project "
  // label in front of it stays plain text. Mirrors the Projects dropdown's
  // own inline-rename pattern in App.js.
  const [headerRenaming, setHeaderRenaming] = useState(false);
  const [headerRenameDraft, setHeaderRenameDraft] = useState(project?.name || '');
  const [headerRenameBusy, setHeaderRenameBusy] = useState(false);
  const [headerRenameError, setHeaderRenameError] = useState(null);

  useEffect(() => {
    setHeaderRenaming(false);
    setHeaderRenameDraft(project?.name || '');
    setHeaderRenameError(null);
  }, [project?.project_id, project?.name]);

  const handleStartRenameHeader = () => {
    setHeaderRenameDraft(project?.name || '');
    setHeaderRenameError(null);
    setHeaderRenaming(true);
  };

  const handleCancelRenameHeader = () => {
    setHeaderRenaming(false);
    setHeaderRenameDraft(project?.name || '');
    setHeaderRenameError(null);
  };

  const handleCommitRenameHeader = async () => {
    const name = headerRenameDraft.trim();
    if (!name || name === project.name) {
      handleCancelRenameHeader();
      return;
    }
    setHeaderRenameBusy(true);
    setHeaderRenameError(null);
    try {
      const updated = await updateProject(project.project_id, { name });
      onProjectUpdated?.(updated);
      // Reaches the copies onProjectUpdated doesn't -- App.js keeps the
      // renamed project in its dropdown list and in
      // pendingProjectForNextConversation, and ChatInterface caches it by
      // id for the composer's rule pills. Same cross-sibling pattern this
      // file already uses everywhere else it mutates a project.
      window.dispatchEvent(new CustomEvent(PROJECT_UPDATED_EVENT, { detail: updated }));
      setHeaderRenaming(false);
    } catch (error) {
      setHeaderRenameError(error.message || 'Failed to rename project');
    } finally {
      setHeaderRenameBusy(false);
    }
  };

  // The project's free-text instructions box -- a draft with an explicit
  // Save, not instant-apply (see the component doc comment above).
  const [instructionsDraft, setInstructionsDraft] = useState(project?.instructions || '');
  // Read-only/greyed once there's saved text to show, so the box reads as
  // "here's what's set" rather than an always-open edit field -- clicking it
  // (see the textarea's own onClick) unlocks it, and saving (or canceling)
  // locks it back. Starts unlocked for a project with nothing saved yet,
  // since there's nothing to "view" and the user is just going to type.
  const [instructionsLocked, setInstructionsLocked] = useState(Boolean(project?.instructions));
  const [instructionsSaving, setInstructionsSaving] = useState(false);
  const [instructionsError, setInstructionsError] = useState(null);
  // Non-null while the "Updated" confirmation is showing -- cleared after
  // 10s by the effect below, same fade-then-forget pattern this panel used
  // for the old rule-checklist's "Applied" status.
  const [instructionsSavedAt, setInstructionsSavedAt] = useState(null);
  // The last value this panel knows the SERVER has -- lets the project-prop
  // sync effect below tell "the user hasn't touched the draft since the last
  // save" (safe to adopt an external change) apart from "there's an unsaved
  // edit in progress" (must not clobber it). See that effect.
  const lastSavedInstructionsRef = useRef(project?.instructions || '');
  // Ticket for the in-flight save. Saving twice quickly fires two
  // overlapping PATCHes; only the newest one's response may be applied, so
  // an earlier reply can't roll the panel back to text the user has since
  // moved past.
  const instructionsSaveSeqRef = useRef(0);

  const [files, setFiles] = useState(() => getCachedProjectFiles(project?.project_id) || []);
  const [filesLoading, setFilesLoading] = useState(() => !hasCachedProjectFiles(project?.project_id));
  const [filesError, setFilesError] = useState(null);
  // Files someone else added since this person last opened the Knowledge
  // base (the server's is_new). Kept for the whole visit: opening the tab
  // records it as seen (see the markProjectFilesSeen effect below), so every
  // later reload reports them as old. Dismiss hides the banner and tags.
  const [newFileIds, setNewFileIds] = useState(() => new Set());
  const newFileIdsRef = useRef(newFileIds);
  newFileIdsRef.current = newFileIds;
  const [newFilesDismissed, setNewFilesDismissed] = useState(false);
  // Clears the Knowledge base tab's dot once that tab has been opened.
  const [newFilesTabOpened, setNewFilesTabOpened] = useState(false);
  // Which project `files` currently holds, and what the last
  // markProjectFilesSeen call covered -- see that effect.
  const filesLoadedForRef = useRef(null);
  const filesSeenMarkRef = useRef({ projectId: null, newCount: 0 });
  const [removingId, setRemovingId] = useState(null);
  const [editingFileId, setEditingFileId] = useState(null);
  const [editingFileName, setEditingFileName] = useState('');
  // Every file currently uploading/indexing (or that just failed), each
  // tracked independently -- [{id, name, status: 'uploading'|'indexing'|
  // 'failed', error, isMedia}]. This used to be a handful of singular
  // uploadStatus/uploadError/uploadIsMedia states shared by whatever upload
  // was "current" -- fine for one file at a time, but a second upload
  // starting before the first finished (e.g. a video still transcribing)
  // would stomp the first's progress and clear it out from under it the
  // moment the second one settled, making the first look like it silently
  // failed even when it was still running fine in the background. Each
  // upload now owns its own entry, so any number can run concurrently
  // (mirrors ProjectSetupModal.js's own addFiles, minus the duplicate-name
  // gate that only matters once a project already has files).
  const [uploads, setUploads] = useState([]);
  const addUpload = (fields) => {
    const id = ++uploadIdSeq;
    setUploads((prev) => [...prev, { id, error: null, spinDelayMs: spinnerSyncDelayMs(), ...fields }]);
    return id;
  };
  const updateUpload = (id, fields) => setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, ...fields } : u)));
  const removeUpload = (id) => setUploads((prev) => prev.filter((u) => u.id !== id));
  // Set only when the user picks "Upload anyway (keep both)" on the
  // duplicate-name dialog below -- a standing reminder, next to the upload
  // list, that they now have two files sharing a name until one is
  // renamed. Cleared at the start of the next processFiles batch so it
  // never lingers past whatever file(s) get picked next.
  const [uploadNote, setUploadNote] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  // A View member dragging a file over the Knowledge base -- see handleDragOver.
  const [dragBlocked, setDragBlocked] = useState(false);
  const fileInputRef = useRef(null);

  // Knowledge base folders -- purely organizational, so the list itself
  // lives on `project.file_folders` (comes back on every ProjectResponse,
  // same as default_rule_ids) rather than a separate fetch/loading state.
  // Which individual folders are expanded -- session-only (not persisted),
  // empty by default, so every folder starts CLOSED on a fresh page
  // load/refresh no matter what was expanded before (same reasoning as
  // ConversationsSidebar.js's own expandedFolderIds).
  const [expandedFileFolderIds, setExpandedFileFolderIds] = useState(() => new Set());
  const [isCreatingFileFolder, setIsCreatingFileFolder] = useState(false);
  // Same "Folders" section collapse as conversationFoldersSectionCollapsed
  // below, for the Knowledge base tab's own file folders.
  const [fileFoldersSectionCollapsed, setFileFoldersSectionCollapsed] = useState(false);
  const [newFileFolderName, setNewFileFolderName] = useState('');
  const [creatingFileFolder, setCreatingFileFolder] = useState(false);
  const [fileFolderError, setFileFolderError] = useState(null);
  const [editingFileFolderId, setEditingFileFolderId] = useState(null);
  const [editingFileFolderName, setEditingFileFolderName] = useState('');
  // Drag-and-drop: filing an existing file into a folder (or back to
  // Unfiled) -- distinct from dragOver above, which is an OS file being
  // dropped in to UPLOAD. See handleDragOver's dataTransfer.types check for
  // how the two are told apart on the same pane.
  const [draggedFileId, setDraggedFileId] = useState(null);
  const [dragOverFolderTarget, setDragOverFolderTarget] = useState(null); // a folder_id, or 'unfiled'
  const [moveMenuFileId, setMoveMenuFileId] = useState(null);
  // Shared by the Knowledge base file menu AND the Chats conversation menu
  // below -- same reasoning ConversationsSidebar.js gives its own single
  // folderSearchText: only one of moveMenuFileId/moveMenuConversationId is
  // ever open at a time (the two tabs are never visible simultaneously), so
  // one shared search box is safe. Matches that same component's search +
  // scroll UI, at the user's explicit request for consistency between them.
  const [moveMenuFolderSearchText, setMoveMenuFolderSearchText] = useState('');

  // The eye-icon preview modal -- null when closed. { kind, name, view_url,
  // download_url } once loaded; loadingName/errorName are set while the
  // request for that one file is in flight, so a slow fetch shows a spinner
  // in the modal itself rather than leaving the eye button looking inert.
  const [filePreview, setFilePreview] = useState(null);
  const [filePreviewLoadingName, setFilePreviewLoadingName] = useState(null);
  const [filePreviewError, setFilePreviewError] = useState(null);
  // Bumped per preview request, so only the latest one opens the modal --
  // an earlier, slower one (another file, or another project) is dropped.
  const filePreviewSeqRef = useRef(0);

  // Local echo of the ACTIVE conversation's knowledge_scope, optimistically
  // updated on checkbox toggle and rolled back on a failed PUT. Kept in
  // sync with the prop below
  // -- re-synced whenever the active conversation itself changes, or when
  // another copy of this panel (or ChatInterface's own listener) updates it.
  const [activeScope, setActiveScope] = useState(activeConversationKnowledgeScope || null);
  const [scopeSaving, setScopeSaving] = useState(false);
  const [scopeSaveError, setScopeSaveError] = useState(null);
  useEffect(() => {
    setActiveScope(activeConversationKnowledgeScope || null);
  }, [activeConversationKnowledgeScope, activeConversationId]);
  // Whether the Files tab is showing checkboxes (an explicit editing step,
  // entered via "Select files") or its normal browse view, where the
  // CURRENTLY SAVED scope is shown as a highlight on each in-scope row/folder
  // instead. `draftScope` is a working copy edited by the checkboxes and only
  // takes effect on "Save" -- ticking one does NOT persist anything, unlike
  // the Rules tab, per explicit request. "Cancel" discards it untouched.
  const [scopeEditMode, setScopeEditMode] = useState(false);
  const [draftScope, setDraftScope] = useState(null);
  // Leaving the project (or switching which conversation is active) drops
  // any unsaved draft rather than silently carrying it to a different
  // context it was never chosen for.
  useEffect(() => {
    setScopeEditMode(false);
    setDraftScope(null);
  }, [project?.project_id, activeConversationId]);

  // Files someone else added after the ACTIVE conversation joined this
  // project, left out of it until the user adds them -- tagged "Not in this
  // chat" in the list below, each with its own "Use in this chat". Fetched
  // when the conversation changes and whenever the file list reloads, and
  // kept in step with ChatInterface's notice (see KNOWLEDGE_HOLD_UPDATED_EVENT).
  const [heldFiles, setHeldFiles] = useState([]);
  const [heldAddingId, setHeldAddingId] = useState(null);
  const activeConversationIdRef = useRef(activeConversationId);
  activeConversationIdRef.current = activeConversationId;
  useEffect(() => { setHeldFiles([]); }, [activeConversationId]);
  useEffect(() => {
    const onHoldUpdated = (e) => {
      const { conversation_id: conversationId, held_files: held } = e.detail || {};
      if (conversationId && conversationId === activeConversationIdRef.current) setHeldFiles(held || []);
    };
    window.addEventListener(KNOWLEDGE_HOLD_UPDATED_EVENT, onHoldUpdated);
    return () => window.removeEventListener(KNOWLEDGE_HOLD_UPDATED_EVENT, onHoldUpdated);
  }, []);
  const heldFileIds = new Set(heldFiles.map((f) => f.file_id));

  const [history, setHistory] = useState([]);
  // Read-only copies of this project's group chats the user is no longer in
  // (left_group): their own conversations, but listed under Group chats.
  const [leftChats, setLeftChats] = useState([]);
  // How many group chats the project has, for the Conversations tab's count:
  // read with the history, and kept up by the Group chats section while it's open.
  const [groupChatCount, setGroupChatCount] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState(null);
  // Filters the Chats tab's own conversation list -- same "Filter
  // conversations" box ConversationsSidebar.js has for the regular history
  // list, at the user's explicit request for consistency between the two.
  const [historySearchText, setHistorySearchText] = useState('');
  const [deletingConversationId, setDeletingConversationId] = useState(null);
  // Group chats (see GroupChat.js): the conversation being made one, and
  // a nudge for the section to re-read its list after.
  const [promoteTarget, setPromoteTarget] = useState(null);
  const [groupChatsRefreshKey, setGroupChatsRefreshKey] = useState(0);
  const [groupChatsNotice, setGroupChatsNotice] = useState(null);
  const [editingConversationId, setEditingConversationId] = useState(null);
  const [editingConversationName, setEditingConversationName] = useState('');
  // Drag-to-reorder the Chats list -- same dataTransfer.setData('text/plain',
  // id) idiom the file-row-into-folder drag already uses, but reordering a
  // flat list instead of filing into a container. draggedOverId tracks which
  // row the pointer is currently over, purely for the drop-target highlight.
  const [draggedConversationId, setDraggedConversationId] = useState(null);
  const [draggedOverConversationId, setDraggedOverConversationId] = useState(null);
  const [reorderError, setReorderError] = useState(null);

  // Conversation history folders -- same "organizational only" shape as the
  // Knowledge base ones above, just for this project's OWN Chats list
  // (project.conversation_folders) instead of its file library. Kept as a
  // fully separate state block rather than generalizing the file-folder one,
  // since the two lists, drag sources, and API endpoints are all distinct.
  // Session-only, empty by default -- same reasoning as expandedFileFolderIds
  // above (closed on every fresh page load/refresh).
  const [expandedConversationFolderIds, setExpandedConversationFolderIds] = useState(() => new Set());
  // Folders the user closed while searching the Chats list -- same as
  // ConversationsSidebar.js's searchClosedFolderIds: a search opens every
  // folder holding a match, a click can still close one, and clearing the
  // search empties this.
  const [searchClosedConversationFolderIds, setSearchClosedConversationFolderIds] = useState(() => new Set());
  useEffect(() => {
    if (!historySearchText.trim()) {
      setSearchClosedConversationFolderIds((prev) => (prev.size ? new Set() : prev));
    }
  }, [historySearchText]);
  // Collapses the WHOLE "Folders" section (all folders at once), separate
  // from expandedConversationFolderIds above (which expands one folder at
  // a time) -- same "Folders" toolbar/heading ConversationsSidebar.js has
  // above its own folder list, at the user's explicit request to replace
  // this tab's standalone "New folder" button with that same pattern.
  const [conversationFoldersSectionCollapsed, setConversationFoldersSectionCollapsed] = useState(false);
  // The same fold for the "General conversations" heading's own list.
  const [generalConversationsCollapsed, setGeneralConversationsCollapsed] = useState(false);
  const [isCreatingConversationFolder, setIsCreatingConversationFolder] = useState(false);
  const [newConversationFolderName, setNewConversationFolderName] = useState('');
  const [creatingConversationFolder, setCreatingConversationFolder] = useState(false);
  const [conversationFolderError, setConversationFolderError] = useState(null);
  const [editingConversationFolderId, setEditingConversationFolderId] = useState(null);
  const [editingConversationFolderName, setEditingConversationFolderName] = useState('');
  const [dragOverConversationFolderTarget, setDragOverConversationFolderTarget] = useState(null); // a folder_id, or 'unfiled'
  const [moveMenuConversationId, setMoveMenuConversationId] = useState(null);

  const [activeTab, setActiveTab] = useState(() => loadStoredTab(project?.project_id) || 'chats');
  const tabRefs = useRef([]);

  // A single styled confirmation dialog shared by every destructive action in
  // this panel (removing a file, deleting a conversation) -- replaces what
  // used to be window.confirm for each, which renders as the browser's own
  // bare native prompt rather than this app's actual dialog design (compare
  // ConversationsSidebar.js's delete-conversation modal, which this matches).
  // { title, message, confirmLabel, busyLabel, onConfirm,
  //   secondaryLabel?, secondaryBusyLabel?, onSecondary? } | null. The
  // secondary fields are optional -- only the duplicate-upload dialog uses
  // them today (its "Replace" button alongside the normal confirm action);
  // every other caller just gets its usual single Cancel/Confirm pair.
  const [confirmModal, setConfirmModal] = useState(null);
  // null | 'primary' | 'secondary' -- which action (if either) is in flight,
  // so the right button shows its busyLabel while both stay disabled.
  const [confirmBusy, setConfirmBusy] = useState(null);

  const closeConfirmModal = () => {
    if (confirmBusy) return;
    // Cancelling/dismissing is itself an exit path some callers need to know
    // about -- e.g. the duplicate-file dialog's caller (processFile) is
    // awaited by processFiles' sequential batch loop, and without this the
    // loop parks forever the moment someone cancels instead of choosing an
    // action, stranding every file queued behind it.
    if (confirmModal?.onDismiss) confirmModal.onDismiss();
    setConfirmModal(null);
  };

  const runConfirmAction = async (which) => {
    if (!confirmModal) return;
    const action = which === 'secondary' ? confirmModal.onSecondary : confirmModal.onConfirm;
    if (!action) return;
    setConfirmBusy(which);
    try {
      await action();
      setConfirmModal(null);
    } catch (error) {
      // The specific action already recorded a user-facing error
      // (setFilesError/setHistoryError) -- leaving the modal open here lets
      // them see that error land in the list behind it and retry without
      // having to re-open this dialog from scratch.
    } finally {
      setConfirmBusy(null);
    }
  };

  const runConfirmModal = () => runConfirmAction('primary');
  const runSecondaryConfirmModal = () => runConfirmAction('secondary');

  // Guards against a response from a previous project landing after the user
  // has already switched to a different one.
  const projectIdRef = useRef(project?.project_id);

  // Switching to a DIFFERENT project resets the panel's transient state --
  // including which tab is showing, back to whatever that project's own tab
  // was last left on (or Chats, for one never opened before).
  useEffect(() => {
    projectIdRef.current = project?.project_id;
    setInstructionsDraft(project?.instructions || '');
    lastSavedInstructionsRef.current = project?.instructions || '';
    setInstructionsLocked(Boolean(project?.instructions));
    setInstructionsError(null);
    setInstructionsSavedAt(null);
    setActiveTab(loadStoredTab(project?.project_id) || 'chats');
    setNewFileIds(new Set());
    setNewFilesDismissed(false);
    setNewFilesTabOpened(false);
    setFileFoldersSectionCollapsed(false);
    setConversationFoldersSectionCollapsed(false);
    setGeneralConversationsCollapsed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.project_id]);

  // A "Folders" heading collapsed when its last folder went would otherwise
  // hide the next folder anyone makes (file folders are shared).
  const fileFolderCount = (project?.file_folders || []).length;
  const conversationFolderCount = (project?.conversation_folders || []).length;
  useEffect(() => {
    if (fileFolderCount === 0) setFileFoldersSectionCollapsed(false);
  }, [fileFolderCount]);
  useEffect(() => {
    if (conversationFolderCount === 0) setConversationFoldersSectionCollapsed(false);
  }, [conversationFolderCount]);

  const selectTab = useCallback((tab) => {
    setActiveTab(tab);
    storeTab(project?.project_id, tab);
  }, [project?.project_id]);

  // Jump to Knowledge base the moment a NEW conversation is started (see
  // newConversationTrigger above) -- that's exactly when picking which files
  // this conversation can search actually matters, before the first message
  // goes out, rather than something the user has to remember to go dig for
  // afterward. Guarded on the ref below so mounting on a project that
  // happens to start with newConversationTrigger already nonzero (or the
  // project-switch effect above resetting activeTab moments earlier) doesn't
  // also fire this -- only an actual INCREASE, from this component's own
  // lifetime, counts as "a new conversation was just started."
  const lastNewConversationTriggerRef = useRef(newConversationTrigger);
  useEffect(() => {
    if (newConversationTrigger !== lastNewConversationTriggerRef.current) {
      lastNewConversationTriggerRef.current = newConversationTrigger;
      selectTab('files');
    }
  }, [newConversationTrigger, selectTab]);

  // Standard ARIA tabs keyboard pattern: arrow keys move focus AND selection
  // between tabs (automatic activation), Home/End jump to the ends. Enter/
  // Space need no special handling -- these are real <button> elements, so
  // the browser already fires a click when either is pressed on a focused one.
  const handleTabKeyDown = (e, index) => {
    let nextIndex = null;
    if (e.key === 'ArrowRight') nextIndex = (index + 1) % TABS.length;
    else if (e.key === 'ArrowLeft') nextIndex = (index - 1 + TABS.length) % TABS.length;
    else if (e.key === 'Home') nextIndex = 0;
    else if (e.key === 'End') nextIndex = TABS.length - 1;
    if (nextIndex === null) return;
    e.preventDefault();
    selectTab(TABS[nextIndex].key);
    tabRefs.current[nextIndex]?.focus();
  };

  // Keep the draft in step with whatever the project actually has saved,
  // including a change made from another copy of this panel -- but only
  // when the user hasn't started typing an unsaved edit of their own (see
  // lastSavedInstructionsRef's own comment above). Adopting an external
  // change out from under an in-progress edit would silently discard it.
  useEffect(() => {
    const incoming = project?.instructions || '';
    setInstructionsDraft((prev) => (
      prev === lastSavedInstructionsRef.current ? incoming : prev
    ));
    lastSavedInstructionsRef.current = incoming;
  }, [project?.instructions]);

  // "Updated" is a confirmation, not a status: let it fade rather than sit
  // there indefinitely claiming something about a change made minutes ago.
  // 10s, not the panel's usual ~2.5s fade, so there's time to actually read
  // "will apply to the whole project" rather than just catch a flicker.
  useEffect(() => {
    if (!instructionsSavedAt) return undefined;
    const timer = setTimeout(() => setInstructionsSavedAt(null), 10000);
    return () => clearTimeout(timer);
  }, [instructionsSavedAt]);

  // Counts every change to `files` (see the effect below), so a background
  // reload can tell whether the list changed here while it was on its way.
  const filesChangeSeqRef = useRef(0);

  const loadFiles = useCallback(async ({ quiet = false } = {}) => {
    if (!project?.project_id) return;
    const requestedFor = project.project_id;
    // Loading placeholders only when there's nothing to show for this
    // project yet. A reload (after an upload, a delete, or reopening a
    // project already seen this session) keeps the list up until the new
    // one arrives, rather than blanking it every time.
    const alreadyShowing = filesLoadedForRef.current === requestedFor;
    // A background reload (the live refresh) touches nothing on screen
    // unless the list actually changed, and never shows an error.
    if (quiet && !alreadyShowing) return;
    const changeSeq = filesChangeSeqRef.current;
    if (!quiet) {
      const cached = alreadyShowing ? null : getCachedProjectFiles(requestedFor);
      if (cached) setFiles(cached);
      setFilesLoading(!alreadyShowing && !cached);
      setFilesError(null);
    }
    try {
      // A first load takes the copy a hover or click in the Projects menu
      // already fetched or started fetching (prefetchProjectFiles), if any.
      const result = await fetchProjectFiles(requestedFor, { firstLoad: !alreadyShowing });
      if (projectIdRef.current !== requestedFor) return;
      // An upload, delete, rename or move landed meanwhile: this list may
      // predate it, and the next refresh will bring it anyway.
      if (quiet && filesChangeSeqRef.current !== changeSeq) return;
      filesLoadedForRef.current = requestedFor;
      setFiles((prev) => (quiet && JSON.stringify(prev) === JSON.stringify(result) ? prev : result));
      const fresh = result.filter((f) => f.is_new).map((f) => f.file_id);
      // Someone added a file while this was open: flag it like on arrival,
      // even if the earlier ones were already looked at or dismissed.
      if (quiet && fresh.some((id) => !newFileIdsRef.current.has(id))) {
        setNewFilesDismissed(false);
        setNewFilesTabOpened(false);
      }
      setNewFileIds((prev) => (fresh.every((id) => prev.has(id)) ? prev : new Set([...prev, ...fresh])));
    } catch (error) {
      if (quiet || projectIdRef.current !== requestedFor) return;
      setFilesError(error.message);
    } finally {
      if (!quiet && projectIdRef.current === requestedFor) setFilesLoading(false);
    }
  }, [project?.project_id]);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  // A delete, rename or folder move updates `files` directly, without a
  // reload; keep the remembered copy in step so reopening the project never
  // briefly shows the list as it was before.
  useEffect(() => {
    filesChangeSeqRef.current += 1;
    const projectId = filesLoadedForRef.current;
    if (projectId && projectId === project?.project_id) setCachedProjectFiles(projectId, files);
  }, [files, project?.project_id]);

  useEffect(() => {
    if (!activeConversationId) return undefined;
    let cancelled = false;
    listHeldFiles(activeConversationId)
      .then((held) => {
        if (cancelled) return;
        window.dispatchEvent(new CustomEvent(KNOWLEDGE_HOLD_UPDATED_EVENT, {
          detail: { conversation_id: activeConversationId, held_files: held },
        }));
      })
      .catch((error) => console.error('Error loading held knowledge base files:', error));
    return () => { cancelled = true; };
  }, [activeConversationId, files]);

  const handleUseHeldFile = async (fileId) => {
    const conversationId = activeConversationId;
    if (!conversationId || heldAddingId) return;
    setHeldAddingId(fileId);
    try {
      const { held_files: held, note } = await answerHeldFiles(conversationId, { accept: [fileId] });
      window.dispatchEvent(new CustomEvent(KNOWLEDGE_HOLD_UPDATED_EVENT, {
        detail: { conversation_id: conversationId, held_files: held, note },
      }));
    } catch (error) {
      if (activeConversationIdRef.current === conversationId) {
        setFilesError(error.message || "Couldn't add that file to this conversation");
      }
    } finally {
      setHeldAddingId(null);
    }
  };

  // A file added to THIS project from somewhere other than this panel's own
  // upload zone -- currently only ProjectSetupModal's -- doesn't otherwise
  // reach this already-mounted instance's file list at all.
  useEffect(() => {
    const onFilesUpdated = (e) => {
      if (e.detail?.project_id === project?.project_id) loadFiles();
    };
    window.addEventListener(PROJECT_FILES_UPDATED_EVENT, onFilesUpdated);
    return () => window.removeEventListener(PROJECT_FILES_UPDATED_EVENT, onFilesUpdated);
  }, [project?.project_id, loadFiles]);

  // Opening the Knowledge base tab counts as having seen what's new there:
  // tell the server once per visit (and again if a reload turned up more),
  // so the next visit only flags files added after this one. Skipped for a
  // project nobody else is in.
  useEffect(() => {
    const projectId = project?.project_id;
    if (activeTab !== 'files' || !projectId || filesLoadedForRef.current !== projectId) return;
    if (newFileIds.size) setNewFilesTabOpened(true);
    const lastMark = filesSeenMarkRef.current;
    if (lastMark.projectId === projectId && lastMark.newCount === newFileIds.size) return;
    if (!project?.shared && !(project?.shared_with > 0) && !newFileIds.size) return;
    filesSeenMarkRef.current = { projectId, newCount: newFileIds.size };
    markProjectFilesSeen(projectId).catch(() => {});
  }, [activeTab, files, newFileIds, project?.project_id, project?.shared, project?.shared_with]);

  // Same as filesChangeSeqRef, for the conversations list.
  const historyChangeSeqRef = useRef(0);
  useEffect(() => { historyChangeSeqRef.current += 1; }, [history]);

  // `quiet`: a background reload, like loadFiles's. Resolves to 'stale' when
  // a quiet reload was dropped because the list changed here meanwhile (a
  // rename, a move), so a caller that needs this reload can run it again.
  const loadHistory = useCallback(async ({ quiet = false } = {}) => {
    if (!project?.project_id) return;
    const requestedFor = project.project_id;
    const changeSeq = historyChangeSeqRef.current;
    if (!quiet) {
      setHistoryLoading(true);
      setHistoryError(null);
    }
    try {
      const [all, groupChats] = await Promise.all([
        listConversations(),
        // Only counted here: a failure leaves the count as it was.
        listProjectGroupChats(requestedFor).catch(() => null),
      ]);
      if (projectIdRef.current !== requestedFor) return;
      if (groupChats) setGroupChatCount(groupChats.length);
      if (quiet && historyChangeSeqRef.current !== changeSeq) return 'stale';
      const inProject = all.filter((c) => c.project_id === requestedFor);
      const list = sortByManualOrder(inProject.filter((c) => !c.left_group));
      const left = inProject.filter((c) => c.left_group);
      setHistory((prev) => (quiet && JSON.stringify(prev) === JSON.stringify(list) ? prev : list));
      setLeftChats((prev) => (JSON.stringify(prev) === JSON.stringify(left) ? prev : left));
    } catch (error) {
      if (quiet || projectIdRef.current !== requestedFor) return;
      setHistoryError(error.message);
    } finally {
      if (!quiet && projectIdRef.current === requestedFor) setHistoryLoading(false);
    }
  }, [project?.project_id]);

  useEffect(() => { loadHistory(); }, [loadHistory, refreshTrigger]);

  // Other people's changes, without a reload: who's in the project, its
  // Knowledge base, its conversations and its own settings, re-read every
  // LIVE_REFRESH_MS while the tab is visible and straight away on coming
  // back to it. A project nobody else is in only refreshes on coming back
  // (another tab of yours may have changed it); listing a big Knowledge base
  // every 30s for nobody's changes would be wasted work. Skipped while the
  // user is mid-action here (see liveRefreshBusyRef), so a reload never lands
  // on an edit, a drag or an upload in progress.
  const liveRefreshBusyRef = useRef(false);
  liveRefreshBusyRef.current = Boolean(
    uploads.some((u) => u.status !== 'failed')
    || headerRenaming || headerRenameBusy || instructionsSaving || scopeEditMode || confirmModal
    || editingFileId || editingFileFolderId || isCreatingFileFolder || creatingFileFolder
    || draggedFileId || moveMenuFileId || removingId
    || editingConversationId || editingConversationFolderId || isCreatingConversationFolder
    || creatingConversationFolder || draggedConversationId || moveMenuConversationId || deletingConversationId
  );
  const liveRefreshSharedRef = useRef(false);
  liveRefreshSharedRef.current = Boolean(
    project?.shared || project?.shared_with > 0 || project?.pending_invites > 0
  );
  useLiveRefresh((trigger) => {
    if (liveRefreshBusyRef.current) return;
    if (trigger === 'timer' && !liveRefreshSharedRef.current) return;
    loadMembers({ quiet: true });
    loadFiles({ quiet: true });
    loadHistory({ quiet: true });
    refreshProject();
  }, { intervalMs: LIVE_REFRESH_MS, enabled: Boolean(project?.project_id) });

  // A reply finished: re-read the list quietly. While the user is mid-action
  // (liveRefreshBusyRef) it waits and runs once they're done, rather than
  // being dropped -- a project nobody else is in has no timer to catch up.
  // The trigger only counts as handled once its reload is applied: one
  // dropped because a rename or move changed the list while it was on its
  // way runs again (after the user is done, if they're still mid-action),
  // and a reply that finishes while one is on its way gets its own after it.
  // loadHistory through a ref so a project switch doesn't re-run it, and the
  // last-seen trigger starts at the mount-time value so mounting doesn't
  // repeat the load the effect above just started.
  const loadHistoryRef = useRef(loadHistory);
  loadHistoryRef.current = loadHistory;
  const activitySeenRef = useRef(conversationActivityTrigger);
  const activityLoadingRef = useRef(false);
  const [activityRecheck, setActivityRecheck] = useState(0);
  const isLiveRefreshBusy = liveRefreshBusyRef.current;
  useEffect(() => {
    if (conversationActivityTrigger === activitySeenRef.current) return;
    if (isLiveRefreshBusy || activityLoadingRef.current) return;
    const trigger = conversationActivityTrigger;
    activityLoadingRef.current = true;
    loadHistoryRef.current({ quiet: true }).then((result) => {
      activityLoadingRef.current = false;
      if (result !== 'stale') activitySeenRef.current = trigger;
      setActivityRecheck((n) => n + 1);
    });
  }, [conversationActivityTrigger, isLiveRefreshBusy, activityRecheck]);

  // Explicit Save (not instant-apply, unlike the checklist this replaced --
  // see the component doc comment above): a big block of free text is worth
  // a moment to review before it goes live for the whole project.
  const handleSaveInstructions = async () => {
    if (!project?.project_id || instructionsSaving) return;
    // Nothing was actually typed -- most likely the user clicked into an
    // already-locked box just to look, then hit Save without changing
    // anything. Still locks it back (matching what Save always visually
    // does) without a pointless PATCH for a no-op change.
    if (instructionsDraft === (project?.instructions || '')) {
      setInstructionsLocked(true);
      return;
    }
    const requestedFor = project.project_id;
    const seq = ++instructionsSaveSeqRef.current;
    setInstructionsSaving(true);
    setInstructionsError(null);
    try {
      const updated = await updateProject(requestedFor, { instructions: instructionsDraft });
      if (instructionsSaveSeqRef.current !== seq || projectIdRef.current !== requestedFor) return;
      onProjectUpdated?.(updated);
      window.dispatchEvent(new CustomEvent(PROJECT_UPDATED_EVENT, { detail: updated }));
      const savedText = updated.instructions || '';
      lastSavedInstructionsRef.current = savedText;
      setInstructionsDraft(savedText);
      setInstructionsSavedAt(Date.now());
      setInstructionsLocked(true);
    } catch (error) {
      if (instructionsSaveSeqRef.current !== seq || projectIdRef.current !== requestedFor) return;
      setInstructionsError(error.message || 'Failed to save instructions');
    } finally {
      if (instructionsSaveSeqRef.current === seq) setInstructionsSaving(false);
    }
  };

  const handleCancelInstructions = () => {
    setInstructionsDraft(project?.instructions || '');
    setInstructionsError(null);
    setInstructionsLocked(Boolean(project?.instructions));
  };

  // Leaving the box never saves -- only Save does. Saving on blur meant any
  // focus loss (clicking DevTools, switching windows, the address bar)
  // published a half-finished edit, so a later Cancel had nothing left to
  // undo. An untouched box just locks back; an edited one stays open with
  // Save / Cancel and an "Unsaved changes" note until the user picks one.
  const handleInstructionsBlur = () => {
    if (instructionsDraft === (project?.instructions || '')) setInstructionsLocked(true);
  };

  // The CURRENTLY SAVED scope -- the ACTIVE conversation's own (once one
  // exists), or the pending one App.js is holding for whichever conversation
  // is about to be created. Drives the browse view's row highlighting. Same
  // "no explicit selection = every file" default the backend applies.
  const savedScope = activeConversationId ? activeScope : (pendingKnowledgeScope || null);

  const handleEnterScopeEditMode = () => {
    setDraftScope(savedScope);
    setScopeSaveError(null);
    setScopeEditMode(true);
  };

  const handleCancelScopeEdit = () => {
    setScopeEditMode(false);
    setDraftScope(null);
    setScopeSaveError(null);
  };

  const toggleFileDraft = (fileId) => {
    setDraftScope((prev) => toggleFileInScope(files, project?.file_folders || [], prev, fileId));
  };
  const toggleFolderDraft = (folderId) => {
    setDraftScope((prev) => toggleFolderInScope(files, project?.file_folders || [], prev, folderId));
  };
  const handleDraftSelectAll = () => setDraftScope(null);
  const handleDraftClear = () => setDraftScope({ mode: 'selected', file_ids: [], folder_ids: [] });

  // "Save" is the only thing that actually persists anything -- ticking a
  // box above only edits draftScope. When there's an active conversation
  // this PUTs .../knowledge-scope and tells ChatInterface via
  // KNOWLEDGE_SCOPE_UPDATED_EVENT (a sibling under ProjectView, unreachable
  // via a normal prop callback -- same reasoning PROJECT_UPDATED_EVENT
  // already established). With no active conversation yet, there is nothing
  // to PUT to -- this just updates the pending value App.js hands to
  // ChatInterface's very first turn.
  const handleSaveScopeEdit = async () => {
    if (!activeConversationId) {
      onPendingKnowledgeScopeChange?.(draftScope);
      setScopeEditMode(false);
      setDraftScope(null);
      return;
    }
    setScopeSaveError(null);
    setScopeSaving(true);
    try {
      const { conversation: updated, note } = await setConversationKnowledgeScope(
        activeConversationId, draftScope,
      );
      setActiveScope(updated.knowledge_scope || null);
      window.dispatchEvent(new CustomEvent(KNOWLEDGE_SCOPE_UPDATED_EVENT, {
        detail: {
          conversation_id: activeConversationId,
          knowledge_scope: updated.knowledge_scope || null,
          note,
        },
      }));
      setScopeEditMode(false);
      setDraftScope(null);
    } catch (error) {
      // Left open on failure -- the user is still looking right at their
      // edited checkboxes to retry, and closing it would silently discard
      // a change they haven't actually managed to save yet.
      setScopeSaveError(error.message || 'Failed to update the knowledge-base scope for this conversation.');
    } finally {
      setScopeSaving(false);
    }
  };

  // The actual upload, split out from processFile below so the duplicate-name
  // confirmation can sit in front of it without duplicating this logic. Not
  // awaited by its callers below -- each upload owns its own `uploads` entry
  // and runs independently, so multiple files (including a slow video
  // transcription) proceed concurrently instead of one blocking the next.
  const runUpload = async (file) => {
    const requestedFor = project.project_id;
    const lowerName = file.name.toLowerCase();
    const isMedia = TRANSCRIBABLE_MEDIA_EXTENSIONS.some((ext) => lowerName.endsWith(ext));
    const id = addUpload({ name: file.name, status: 'uploading', isMedia });
    let failed = false;
    try {
      await uploadProjectFile(requestedFor, file, (status) => {
        if (projectIdRef.current === requestedFor) updateUpload(id, { status });
      });
    } catch (error) {
      failed = true;
      if (projectIdRef.current === requestedFor) updateUpload(id, { status: 'failed', error: error.message });
    }
    // Refreshed even on failure, deliberately: the most likely failure is
    // uploadProjectFile giving up on its poll, and indexing carries on in
    // the background regardless -- so the document IS in the library, and
    // the list can show it as "Indexing…" instead of nothing at all.
    if (projectIdRef.current === requestedFor) await loadFiles();
    // A failed entry stays visible (with its error and a dismiss button, see
    // the render below) instead of disappearing along with everything else --
    // a successful upload's own entry is removed here since the file itself,
    // now in the list below, IS its own "done" indicator.
    if (!failed && projectIdRef.current === requestedFor) removeUpload(id);
  };

  // Deletes the old file, then fires off the replacement's upload -- NOT
  // awaited here, for the same reason runUpload above isn't: a slow upload
  // (especially a video transcription) shouldn't hold the duplicate-file
  // confirm dialog open, or block starting any other file in the same
  // batch. A delete failure is still awaited/thrown, so the dialog can stay
  // open for that (matching handleRemoveFile's own pattern) -- only the
  // upload itself, and the folder-carryover after it, run detached.
  const replaceFile = async (oldFile, newFile) => {
    const requestedFor = project.project_id;
    try {
      await deleteProjectFile(requestedFor, oldFile.file_id);
      if (projectIdRef.current === requestedFor) {
        setFiles((prev) => prev.filter((f) => f.file_id !== oldFile.file_id));
      }
    } catch (error) {
      if (projectIdRef.current === requestedFor) setFilesError(error.message);
      throw error; // keeps the dialog open, matching handleRemoveFile's own pattern
    }

    (async () => {
      await runUpload(newFile);

      if (oldFile.folder_id && projectIdRef.current === requestedFor) {
        try {
          const latest = await listProjectFiles(requestedFor);
          const uploaded = latest.find((f) => f.name?.toLowerCase() === newFile.name.toLowerCase());
          if (uploaded && projectIdRef.current === requestedFor) {
            await setProjectFileFolder(requestedFor, uploaded.file_id, oldFile.folder_id);
            await loadFiles();
          }
        } catch (error) {
          // Non-fatal -- the replacement file exists and is searchable either
          // way, it just landed Unfiled instead of back in its old folder.
        }
      }
    })();
  };

  // Shared by both upload paths below (the file picker and dropping onto the
  // Files pane's own drop row). Returns a promise that resolves once this
  // file has cleared the duplicate-name gate (immediately, for the common
  // non-duplicate case) -- NOT once its upload has finished, which is what
  // lets processFiles below move on to the next file in a batch right away
  // instead of waiting out however long this one's upload/transcription
  // takes. The one thing still serialized is the gate itself: a duplicate
  // opens a confirm dialog, and this panel only has room for one of those
  // at a time (see confirmModal), so processFiles awaits this promise
  // before starting the next file's own check.
  const processFile = (file) => new Promise((resolve) => {
    if (!file) { resolve(); return; }
    const lowerName = file.name.toLowerCase();
    if (!ALL_UPLOAD_EXTENSIONS.some((ext) => lowerName.endsWith(ext))) {
      addUpload({
        name: file.name, status: 'failed',
        error: `Unsupported file type. Allowed: ${ALL_UPLOAD_EXTENSIONS.join(', ')}`,
      });
      resolve();
      return;
    }

    // Name-only check against the library already loaded in this panel --
    // there is no content-hash check here (that dedup lives server-side and
    // only ever saves re-embedding cost, see pdf_index_service.py). This is
    // purely "you probably didn't mean to upload this twice", so it's a
    // dismissible warning, not a block: either action below just re-runs the
    // normal upload/delete flows exactly as if triggered by hand.
    const duplicate = files.find((f) => f.name?.toLowerCase() === lowerName);
    if (duplicate) {
      setConfirmModal({
        title: 'File already in this project?',
        subject: duplicate.name,
        subjectIcon: <ModalFileIcon name={duplicate.name} />,
        message: "A file with this name is already in this project's knowledge base. Replace it, or keep both if this one isn't meant to replace it.",
        confirmLabel: 'Upload anyway (keep both)',
        confirmVariant: 'primary', // not destructive, unlike every other caller of this dialog
        busyLabel: 'Uploading…',
        onConfirm: () => {
          setUploadNote(`This project now has more than one file named "${duplicate.name}" - worth renaming so they're easy to tell apart.`);
          runUpload(file); // not awaited -- see this function's own doc comment
          resolve();
        },
        secondaryLabel: 'Replace',
        secondaryBusyLabel: 'Replacing…',
        onSecondary: async () => {
          await replaceFile(duplicate, file); // only the delete step is awaited inside it
          resolve();
        },
        // Cancelling (Cancel button or backdrop click) means "skip this
        // file" -- it must still resolve, or the rest of a multi-file batch
        // queued behind it in processFiles never runs.
        onDismiss: () => resolve(),
      });
      return;
    }

    runUpload(file); // not awaited -- see this function's own doc comment
    resolve();
  });

  // Runs every file in a batch (a multi-select from the file picker, or a
  // multi-file drop) through processFile's duplicate-name gate one at a
  // time, so at most one confirm dialog is ever open -- but each file's
  // actual upload starts the moment IT clears the gate, not after the
  // whole batch has, so a slow file (a video transcription) never delays
  // the others.
  const processFiles = async (fileList) => {
    setUploadNote(null);
    for (const file of Array.from(fileList || [])) {
      // eslint-disable-next-line no-await-in-loop -- deliberately sequential; see this function's own doc comment.
      await processFile(file);
    }
  };

  const handleFileInputChange = (e) => {
    // Copied to a plain array BEFORE resetting .value below -- e.target.files
    // is a live FileList tied to the input's own current selection, and
    // clearing .value empties that same FileList out from under a held
    // reference to it (a single File object, the old single-file code's own
    // e.target.files?.[0], is unaffected by this -- only the LIST itself is).
    const selectedFiles = Array.from(e.target.files || []);
    // Always reset so selecting the SAME file(s) again still fires onChange.
    e.target.value = '';
    processFiles(selectedFiles);
  };

  // Gated on dataTransfer carrying real OS files -- an in-app drag of an
  // existing file row onto a folder (see handleFileDragStart below) only
  // ever sets 'text/plain', so it can never be mistaken for an upload here.
  const isOsFileDrag = (e) => Array.from(e.dataTransfer.types || []).includes('Files');

  // View can't add files. ChatInterface's window-level dragover handler
  // would otherwise give this pane the same "copy" cursor as everywhere
  // else, so a View member gets the browser's "not allowed" cursor here
  // instead (dropEffect 'none', so no drop fires) and a line saying why.
  const handleDragOver = (e) => {
    if (!isOsFileDrag(e)) return;
    e.preventDefault();
    if (!canEdit) {
      e.dataTransfer.dropEffect = 'none';
      setDragBlocked(true);
      return;
    }
    setDragOver(true);
  };
  const handleDragLeave = () => {
    setDragOver(false);
    setDragBlocked(false);
  };
  const handleDrop = (e) => {
    setDragBlocked(false);
    if (!isOsFileDrag(e)) return;
    // Even for View: a folder header stops propagation before calling this,
    // so the window-level guard can't stop the browser opening the file.
    e.preventDefault();
    setDragOver(false);
    if (!canEdit) return;
    // Copied to a plain array synchronously, same reasoning as
    // handleFileInputChange above -- a DataTransfer's own .files is only
    // guaranteed valid for the duration of this event handler.
    processFiles(Array.from(e.dataTransfer.files || []));
  };

  const handleOpenFilePreview = async (file) => {
    const requestedFor = project.project_id;
    const seq = ++filePreviewSeqRef.current;
    const isCurrent = () => filePreviewSeqRef.current === seq && projectIdRef.current === requestedFor;
    setFilePreviewError(null);
    setFilePreviewLoadingName(file.name);
    try {
      const info = await getProjectFilePreview(requestedFor, file.file_id);
      if (!isCurrent()) return;
      if (!info) throw new Error('No preview available for this file');
      setFilePreview(info);
    } catch (error) {
      if (isCurrent()) setFilePreviewError(error.message);
    } finally {
      if (filePreviewSeqRef.current === seq) setFilePreviewLoadingName(null);
    }
  };

  const handleRemoveFile = (fileId, name) => {
    setConfirmModal({
      title: 'Remove file?',
      subject: name,
      subjectIcon: <ModalFileIcon name={name} />,
      message: 'This file and its search index will be permanently removed from this project. This cannot be undone.',
      confirmLabel: 'Remove',
      busyLabel: 'Removing…',
      onConfirm: async () => {
        setRemovingId(fileId);
        try {
          await deleteProjectFile(project.project_id, fileId);
          setFiles((prev) => prev.filter((f) => f.file_id !== fileId));
          // Nothing tracks which file(s) a stale uploadNote's rename hint refers
          // to, so any delete just clears it rather than risk leaving it pointing
          // at files that no longer exist.
          setUploadNote(null);
        } catch (error) {
          setFilesError(error.message);
          throw error; // keeps the modal open so the error (shown in the list behind it) is easy to retry
        } finally {
          setRemovingId(null);
        }
      },
    });
  };

  const handleStartEditFile = (file) => {
    setEditingFileId(file.file_id);
    setEditingFileName(file.name);
  };

  const handleCancelEditFile = () => {
    setEditingFileId(null);
    setEditingFileName('');
  };

  const handleSaveEditFile = async (fileId) => {
    const name = editingFileName.trim();
    if (!name) return;
    try {
      await renameProjectFile(project.project_id, fileId, name);
      setFiles((prev) => prev.map((f) => (f.file_id === fileId ? { ...f, name } : f)));
      setEditingFileId(null);
      setEditingFileName('');
      setUploadNote(null); // renaming is exactly what the note suggested doing

    } catch (error) {
      setFilesError(error.message || 'Failed to rename file');
    }
  };

  const toggleFileFolderCollapsed = (folderId) => {
    setExpandedFileFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  };

  const handleCreateFileFolder = async () => {
    const name = newFileFolderName.trim();
    if (!name) return;
    setCreatingFileFolder(true);
    setFileFolderError(null);
    try {
      const updated = await createProjectFileFolder(project.project_id, name);
      onProjectUpdated?.(updated);
      window.dispatchEvent(new CustomEvent(PROJECT_UPDATED_EVENT, { detail: updated }));
      setNewFileFolderName('');
      setIsCreatingFileFolder(false);
      // Show the new folder, even under a heading collapsed before.
      setFileFoldersSectionCollapsed(false);
    } catch (error) {
      setFileFolderError(error.message || 'Failed to create folder');
    } finally {
      setCreatingFileFolder(false);
    }
  };

  const handleCancelCreateFileFolder = () => {
    setIsCreatingFileFolder(false);
    setNewFileFolderName('');
    setFileFolderError(null);
  };

  const handleStartEditFileFolder = (folder) => {
    setEditingFileFolderId(folder.folder_id);
    setEditingFileFolderName(folder.name);
  };

  const handleCancelEditFileFolder = () => {
    setEditingFileFolderId(null);
    setEditingFileFolderName('');
  };

  const handleSaveFileFolderEdit = async (folderId) => {
    const name = editingFileFolderName.trim();
    if (!name) return;
    try {
      const updated = await renameProjectFileFolder(project.project_id, folderId, name);
      onProjectUpdated?.(updated);
      window.dispatchEvent(new CustomEvent(PROJECT_UPDATED_EVENT, { detail: updated }));
      setEditingFileFolderId(null);
      setEditingFileFolderName('');
    } catch (error) {
      setFileFolderError(error.message || 'Failed to rename folder');
    }
  };

  const handleDeleteFileFolder = (folder) => {
    setConfirmModal({
      title: 'Delete folder?',
      subject: folder.name,
      subjectIcon: <FiFolder className="project-panel-modal-subject-icon muted" />,
      message: 'This folder will be deleted. Its files are unfiled, not deleted.',
      confirmLabel: 'Delete',
      busyLabel: 'Deleting…',
      onConfirm: async () => {
        try {
          const result = await deleteProjectFileFolder(project.project_id, folder.folder_id);
          onProjectUpdated?.(result.project);
          window.dispatchEvent(new CustomEvent(PROJECT_UPDATED_EVENT, { detail: result.project }));
          // Reflects "unfiled" the instant the response lands, rather than
          // waiting on a full loadFiles() round trip for files we're
          // already holding locally.
          setFiles((prev) =>
            prev.map((f) => (f.folder_id === folder.folder_id ? { ...f, folder_id: null } : f))
          );
          setExpandedFileFolderIds((prev) => {
            if (!prev.has(folder.folder_id)) return prev;
            const next = new Set(prev);
            next.delete(folder.folder_id);
            return next;
          });
        } catch (error) {
          setFilesError(error.message || 'Failed to delete folder');
          throw error; // keeps the modal open so the error is easy to retry
        }
      },
    });
  };

  // Shared by drag-and-drop (below) and the per-file "Move to folder" menu --
  // both just need "this file, this folder_id (or null for Unfiled)".
  // Optimistic, with rollback on failure.
  const moveFileToFolder = async (fileId, folderId) => {
    const file = files.find((f) => f.file_id === fileId);
    if (!file || (file.folder_id || null) === (folderId || null)) return;
    const previousFolderId = file.folder_id || null;
    setFiles((prev) =>
      prev.map((f) => (f.file_id === fileId ? { ...f, folder_id: folderId } : f))
    );
    try {
      await setProjectFileFolder(project.project_id, fileId, folderId);
    } catch (error) {
      setFiles((prev) =>
        prev.map((f) => (f.file_id === fileId ? { ...f, folder_id: previousFolderId } : f))
      );
      setFilesError(error.message || 'Failed to move file');
    }
  };

  const handleFileDragStart = (e, fileId) => {
    setDraggedFileId(fileId);
    e.dataTransfer.setData('text/plain', fileId);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleFileDragEnd = () => {
    setDraggedFileId(null);
    setDragOverFolderTarget(null);
  };
  // A file from the computer (not a file row being filed) is handled exactly
  // as the pane handles it -- these targets stop propagation, so the pane's
  // own handlers would otherwise never see it over a folder.
  const handleFolderTargetDragOver = (e, target) => {
    if (!draggedFileId) {
      handleDragOver(e);
      return;
    }
    e.preventDefault();
    setDragOverFolderTarget(target);
  };
  const handleFolderTargetDragLeave = (target) => {
    setDragOverFolderTarget((prev) => (prev === target ? null : prev));
  };
  const handleFolderTargetDrop = async (e, target) => {
    if (!draggedFileId && isOsFileDrag(e)) {
      handleDrop(e);
      return;
    }
    e.preventDefault();
    const fileId = draggedFileId || e.dataTransfer.getData('text/plain');
    setDraggedFileId(null);
    setDragOverFolderTarget(null);
    if (!fileId) return;
    await moveFileToFolder(fileId, target === 'unfiled' ? null : target);
  };

  const toggleMoveMenuFor = (fileId) => {
    setMoveMenuFileId((prev) => (prev === fileId ? null : fileId));
    setMoveMenuFolderSearchText('');
  };
  const handleMoveFileTo = async (fileId, folderId) => {
    setMoveMenuFileId(null);
    setMoveMenuFolderSearchText('');
    await moveFileToFolder(fileId, folderId);
  };

  // Same helper as ConversationsSidebar.js's own getFolderMatches -- "Unfiled"
  // is deliberately never part of this filtered list (see both render
  // functions below), always shown as its own fixed option underneath it.
  const getMoveMenuFolderMatches = (allFolders, excludeFolderId) => {
    const base = allFolders.filter((folder) => folder.folder_id !== excludeFolderId);
    const q = moveMenuFolderSearchText.trim().toLowerCase();
    if (!q) return base;
    return base.filter((folder) => folder.name.toLowerCase().includes(q));
  };

  // Close an open "Move to folder" menu on an outside click -- same pattern
  // ConversationsSidebar's own move menu uses. Covers both the Knowledge
  // base's per-file menu and the Chats tab's per-conversation one below,
  // since both live in `.project-file-move-wrapper` and only one is ever
  // open at a time (the two tabs are never visible simultaneously).
  useEffect(() => {
    if (!moveMenuFileId && !moveMenuConversationId) return undefined;
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.project-file-move-wrapper')) {
        setMoveMenuFileId(null);
        setMoveMenuConversationId(null);
        setMoveMenuFolderSearchText('');
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [moveMenuFileId, moveMenuConversationId]);

  // Deleting the conversation itself, not just unfiling it from the project.
  const handleDeleteConversation = (conversation) => {
    const label = conversation.name || 'this conversation';
    setConfirmModal({
      title: 'Delete conversation?',
      subject: label,
      subjectIcon: <FiMessageSquare className="project-panel-modal-subject-icon muted" />,
      message: 'This conversation and all its messages will be permanently deleted. This cannot be undone.',
      confirmLabel: 'Delete',
      busyLabel: 'Deleting…',
      onConfirm: async () => {
        setDeletingConversationId(conversation.conversation_id);
        try {
          await deleteConversation(conversation.conversation_id);
          setHistory((prev) =>
            prev.filter((c) => c.conversation_id !== conversation.conversation_id)
          );
          // The chat pane may be showing the very conversation just deleted, and
          // the Conversations drawer counts it too -- neither can find that out on
          // its own, so tell the parent.
          onConversationDeleted?.(conversation.conversation_id);
        } catch (error) {
          setHistoryError(error.message || 'Failed to delete conversation');
          throw error; // keeps the modal open so the error is easy to retry
        } finally {
          setDeletingConversationId(null);
        }
      },
    });
  };

  // Renaming a conversation from its own History row -- same inline-edit
  // idiom (and CSS classes) as a Knowledge base folder's rename just above,
  // rather than a separate modal.
  const handleStartEditConversation = (conversation) => {
    setEditingConversationId(conversation.conversation_id);
    setEditingConversationName(conversation.name);
  };

  const handleCancelEditConversation = () => {
    setEditingConversationId(null);
    setEditingConversationName('');
  };

  const handleSaveEditConversation = async (conversationId) => {
    const name = editingConversationName.trim();
    if (!name) return;
    try {
      await updateConversationName(conversationId, name);
      setHistory((prev) =>
        prev.map((c) => (c.conversation_id === conversationId ? { ...c, name } : c))
      );
      setEditingConversationId(null);
      setEditingConversationName('');
    } catch (error) {
      setHistoryError(error.message || 'Failed to rename conversation');
    }
  };

  const handleConversationDragStart = (e, conversationId) => {
    setDraggedConversationId(conversationId);
    e.dataTransfer.setData('text/plain', conversationId);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleConversationDragEnd = () => {
    setDraggedConversationId(null);
    setDraggedOverConversationId(null);
    setDragOverConversationFolderTarget(null);
  };
  const handleConversationDragOver = (e, conversationId) => {
    if (!draggedConversationId || draggedConversationId === conversationId) return;
    e.preventDefault();
    setDraggedOverConversationId(conversationId);
  };
  const handleConversationDragLeave = (conversationId) => {
    setDraggedOverConversationId((prev) => (prev === conversationId ? null : prev));
  };

  // Dropping ON a row moves the dragged conversation to that row's position
  // (pushing the rest down/up), the usual "drop between/on items" reorder
  // idiom. Optimistic, with rollback on a failed PUT -- same pattern
  // moveFileToFolder uses for filing a file into a folder.
  const handleConversationDrop = async (e, targetConversationId) => {
    e.preventDefault();
    const draggedId = draggedConversationId || e.dataTransfer.getData('text/plain');
    setDraggedConversationId(null);
    setDraggedOverConversationId(null);
    if (!draggedId || draggedId === targetConversationId) return;

    const previousHistory = history;
    const fromIndex = history.findIndex((c) => c.conversation_id === draggedId);
    const toIndex = history.findIndex((c) => c.conversation_id === targetConversationId);
    if (fromIndex === -1 || toIndex === -1) return;
    // Reordering only makes sense within the same group (both Unfiled, or
    // both the same folder) -- dragging a row from one folder onto a row in
    // a DIFFERENT one goes through the folder header's own drop target
    // instead (see handleConversationFolderTargetDrop), which files it
    // rather than just repositioning it.
    if ((history[fromIndex].project_folder_id || null) !== (history[toIndex].project_folder_id || null)) {
      return;
    }

    const reordered = [...history];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    setHistory(reordered);
    setReorderError(null);
    try {
      await reorderProjectConversations(
        project.project_id, reordered.map((c) => c.conversation_id),
      );
    } catch (error) {
      setHistory(previousHistory);
      setReorderError(error.message || 'Failed to reorder conversations');
    }
  };

  // --- Conversation history folders ------------------------------------
  // Mirrors the Knowledge base folder handlers above almost exactly, just
  // against project.conversation_folders / a conversation's project_folder_id
  // instead of project.file_folders / a file's folder_id.

  const toggleConversationFolderCollapsed = (folderId, openForSearch) => {
    (openForSearch ? setSearchClosedConversationFolderIds : setExpandedConversationFolderIds)((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  };

  const handleCreateConversationFolder = async () => {
    const name = newConversationFolderName.trim();
    if (!name) return;
    setCreatingConversationFolder(true);
    setConversationFolderError(null);
    try {
      const updated = await createProjectConversationFolder(project.project_id, name);
      onProjectUpdated?.(updated);
      window.dispatchEvent(new CustomEvent(PROJECT_UPDATED_EVENT, { detail: updated }));
      setNewConversationFolderName('');
      setIsCreatingConversationFolder(false);
      // Show the new folder, even under a heading collapsed before.
      setConversationFoldersSectionCollapsed(false);
    } catch (error) {
      setConversationFolderError(error.message || 'Failed to create folder');
    } finally {
      setCreatingConversationFolder(false);
    }
  };

  const handleCancelCreateConversationFolder = () => {
    setIsCreatingConversationFolder(false);
    setNewConversationFolderName('');
    setConversationFolderError(null);
  };

  const handleStartEditConversationFolder = (folder) => {
    setEditingConversationFolderId(folder.folder_id);
    setEditingConversationFolderName(folder.name);
  };

  const handleCancelEditConversationFolder = () => {
    setEditingConversationFolderId(null);
    setEditingConversationFolderName('');
  };

  const handleSaveConversationFolderEdit = async (folderId) => {
    const name = editingConversationFolderName.trim();
    if (!name) return;
    try {
      const updated = await renameProjectConversationFolder(project.project_id, folderId, name);
      onProjectUpdated?.(updated);
      window.dispatchEvent(new CustomEvent(PROJECT_UPDATED_EVENT, { detail: updated }));
      setEditingConversationFolderId(null);
      setEditingConversationFolderName('');
    } catch (error) {
      setConversationFolderError(error.message || 'Failed to rename folder');
    }
  };

  const handleDeleteConversationFolder = (folder) => {
    setConfirmModal({
      title: 'Delete folder?',
      subject: folder.name,
      subjectIcon: <FiFolder className="project-panel-modal-subject-icon muted" />,
      message: 'This folder will be deleted. Its conversations are unfiled, not deleted.',
      confirmLabel: 'Delete',
      busyLabel: 'Deleting…',
      onConfirm: async () => {
        try {
          const result = await deleteProjectConversationFolder(project.project_id, folder.folder_id);
          onProjectUpdated?.(result.project);
          window.dispatchEvent(new CustomEvent(PROJECT_UPDATED_EVENT, { detail: result.project }));
          setHistory((prev) =>
            prev.map((c) => (c.project_folder_id === folder.folder_id ? { ...c, project_folder_id: null } : c))
          );
          setExpandedConversationFolderIds((prev) => {
            if (!prev.has(folder.folder_id)) return prev;
            const next = new Set(prev);
            next.delete(folder.folder_id);
            return next;
          });
        } catch (error) {
          setHistoryError(error.message || 'Failed to delete folder');
          throw error; // keeps the modal open so the error is easy to retry
        }
      },
    });
  };

  // Shared by drag-and-drop (below) and the per-conversation "Move to
  // folder" menu -- both just need "this conversation, this folder_id (or
  // null for Unfiled)". Optimistic, with rollback on failure, same pattern
  // moveFileToFolder uses for filing a file into a folder.
  const moveConversationToFolder = async (conversationId, folderId) => {
    const conversation = history.find((c) => c.conversation_id === conversationId);
    if (!conversation || (conversation.project_folder_id || null) === (folderId || null)) return;
    const previousFolderId = conversation.project_folder_id || null;
    setHistory((prev) =>
      prev.map((c) => (c.conversation_id === conversationId ? { ...c, project_folder_id: folderId } : c))
    );
    try {
      await setProjectConversationFolder(project.project_id, conversationId, folderId);
    } catch (error) {
      setHistory((prev) =>
        prev.map((c) => (c.conversation_id === conversationId ? { ...c, project_folder_id: previousFolderId } : c))
      );
      setHistoryError(error.message || 'Failed to move conversation');
    }
  };

  // A conversation row is ALREADY draggable for reordering (see
  // handleConversationDragStart) -- these reuse that same draggedConversationId
  // to also support dropping it directly onto a folder header/Unfiled
  // dropzone, the exact "one drag source, two kinds of drop target" split
  // handleFileDragStart/handleFolderTargetDrop already establishes for files.
  const handleConversationFolderTargetDragOver = (e, target) => {
    if (!draggedConversationId) return;
    e.preventDefault();
    setDragOverConversationFolderTarget(target);
  };
  const handleConversationFolderTargetDragLeave = (target) => {
    setDragOverConversationFolderTarget((prev) => (prev === target ? null : prev));
  };
  const handleConversationFolderTargetDrop = async (e, target) => {
    e.preventDefault();
    const conversationId = draggedConversationId || e.dataTransfer.getData('text/plain');
    setDraggedConversationId(null);
    setDragOverConversationFolderTarget(null);
    if (!conversationId) return;
    await moveConversationToFolder(conversationId, target === 'unfiled' ? null : target);
  };

  const toggleMoveMenuConversationFor = (conversationId) => {
    setMoveMenuConversationId((prev) => (prev === conversationId ? null : conversationId));
    setMoveMenuFolderSearchText('');
  };
  const handleMoveConversationTo = async (conversationId, folderId) => {
    setMoveMenuConversationId(null);
    setMoveMenuFolderSearchText('');
    await moveConversationToFolder(conversationId, folderId);
  };

  if (!project) return null;

  const fileFolders = project.file_folders || [];
  const unfiledFiles = files.filter((f) => !f.folder_id);
  // The SAVED scope only -- never the draft being actively edited -- so a
  // file/folder picked for this conversation floats to the top of its own
  // section (folders among folders, unfiled files among unfiled files) once
  // the selection is actually saved, not while the user is still checking
  // boxes. Reordering live, mid-edit, made a just-checked row jump away
  // from the cursor instead of staying put for the next click. A no-op
  // whenever nothing is explicitly restricted yet (isFileInScope/
  // isFolderInScope both default to "everything's in scope"), so this never
  // reorders anything until a selection has actually been saved. Kept as
  // separate sorted arrays rather than mutating fileFolders/unfiledFiles
  // themselves -- the move-to-folder menus below read the latter in their
  // original, stable order, unrelated to this conversation's scope.
  const sortedFileFolders = [...fileFolders].sort(
    (a, b) => Number(!isFolderInScope(a, savedScope)) - Number(!isFolderInScope(b, savedScope))
  );
  const sortedUnfiledFiles = [...unfiledFiles].sort(
    (a, b) => Number(!isFileInScope(a, savedScope)) - Number(!isFileInScope(b, savedScope))
  );
  const conversationFolders = project.conversation_folders || [];
  // Same case-insensitive substring match as ConversationsSidebar.js's own
  // filterText -- name and focus mode tag ("3190", "proposal"); a project's
  // own history has no last-message preview to also search, unlike the
  // regular sidebar's version.
  const historySearchQuery = historySearchText.trim().toLowerCase();
  const searchedHistory = historySearchQuery
    ? history.filter((c) => c.name.toLowerCase().includes(historySearchQuery)
      || focusModeTagMatches(c.focus_mode, historySearchQuery))
    : history;
  // `history` is already sorted (see loadHistory/sortByManualOrder) and kept
  // that way through an optimistic drag-reorder splice (handleConversationDrop)
  // -- filtering here must preserve its CURRENT order rather than re-sorting,
  // otherwise a just-dropped reorder would visually snap back until the
  // backend's response (with real sort_order values) round-trips.
  // A conversation whose project_folder_id isn't one of the viewer's own
  // folders (e.g. filed while folders were still shared project-wide) counts
  // as unfiled -- otherwise it would match no folder group and vanish.
  const conversationFolderIds = new Set(conversationFolders.map((f) => f.folder_id));
  const unfiledHistory = searchedHistory.filter(
    (c) => !c.project_folder_id || !conversationFolderIds.has(c.project_folder_id)
  );
  // While actively searching, hide a folder that neither matches by name nor
  // contains a match -- same reasoning as ConversationsSidebar.js's own
  // visibleFolders.
  const visibleConversationFolders = historySearchQuery
    ? conversationFolders.filter((folder) =>
        folder.name.toLowerCase().includes(historySearchQuery)
        || searchedHistory.some((c) => c.project_folder_id === folder.folder_id))
    : conversationFolders;
  // Whether the SAVED scope currently excludes anything at all -- gates the
  // tab count's "how many are ON" meaning and the empty-selection hint.
  const scopeIsRestricted = Boolean(savedScope && savedScope.mode === 'selected');

  // No badge on Instructions -- it's a single free-text field, not a count
  // of anything (rules was the number of attached rules; a length or a
  // present/absent flag wouldn't tell the user anything useful at a glance).
  // Files mirrors the same "how many are ON" meaning Rules used to, once a
  // scope actually restricts anything, rather than always just the
  // library's total size.
  const tabCount = {
    rules: 0,
    files: scopeIsRestricted ? files.filter((f) => isFileInScope(f, savedScope)).length : files.length,
    // Everything the tab lists: group chats (and copies of ones left), then
    // the user's own conversations.
    chats: groupChatCount + leftChats.length + history.length,
  };

  // "Added by …" under each file once more than one person is in the
  // project -- the same test the header's avatar stack uses, which also means
  // the names have loaded. Someone who has since left reads as "a former
  // member"; a file from before uploaders were recorded says nothing.
  const showUploaders = members.length > 1;
  const uploaderName = (userId) => {
    if (user?.id && userId === user.id) return 'you';
    const member = members.find((m) => m.user_id === userId);
    return member ? (member.display_name || member.email || 'a member') : 'a former member';
  };
  const fileAddedByLine = (file) => {
    if (!showUploaders || !file.uploaded_by) return null;
    const when = file.created_at ? formatDate(file.created_at * 1000) : '';
    return `Added by ${uploaderName(file.uploaded_by)}${when ? ` · ${when}` : ''}`;
  };

  // Who added what since the last visit, then whether THIS conversation will
  // use it: one narrowed to selected files only picks a new file up if it
  // landed in a selected folder.
  const newFiles = newFilesDismissed ? [] : files.filter((f) => newFileIds.has(f.file_id));
  let newFilesNotice = null;
  if (newFiles.length) {
    const names = [...new Set(newFiles.map((f) => uploaderName(f.uploaded_by)))];
    const who = names.length === 1 ? names[0]
      : names.length === 2 ? `${names[0]} and ${names[1]}` : `${names.length} people`;
    const them = newFiles.length === 1 ? 'it' : 'them';
    newFilesNotice = {
      who: `${who.charAt(0).toUpperCase()}${who.slice(1)}`,
      what: newFiles.length === 1 ? 'a new file' : `${newFiles.length} new files`,
      // One file gets its name on a line of its own; several are already
      // tagged "New" in the list below.
      fileName: newFiles.length === 1 ? newFiles[0].name : null,
      usable: newFiles.some((f) => heldFileIds.has(f.file_id))
        ? `This conversation started before ${newFiles.length === 1 ? 'it was' : 'they were'} added, so KAI won't use ${them} here until you choose "Use in this chat".`
        : scopeIsRestricted && newFiles.some((f) => !isFileInScope(f, savedScope))
          ? `This conversation only uses the files you've selected, so select ${them} to use ${them} here.`
          : activeConversationId
            ? `KAI can use ${them} in this conversation.`
            : `New conversations in this project can use ${them}.`,
    };
  }

  // Both tabs' "New folder" button: on the "Folders" heading once there is
  // a folder, until then on "Files" / "General conversations".
  const renderNewFolderButton = (startCreating) => (
    <button
      type="button"
      className="folder-create-btn"
      onClick={(e) => {
        e.stopPropagation();
        startCreating();
      }}
      title="New folder"
      aria-label="New folder"
    >
      <FiFolderPlus size={14} />
    </button>
  );

  // Both tabs' folder-name form, with its error. Opens under whichever
  // heading holds the button; inGrid when that's the one inside the grid.
  const renderFolderCreateForm = ({ value, setValue, onSave, onCancel, saving, error, inGrid }) => {
    const span = inGrid ? ' project-panel-grid-span' : '';
    return (
      <>
        <div className={`project-folder-create-form${span}`}>
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSave();
              if (e.key === 'Escape') onCancel();
            }}
            placeholder="Folder name"
            autoFocus
            className="project-folder-create-input"
          />
          <div className="project-folder-create-buttons">
            <button
              type="button"
              className="project-folder-save-btn"
              onClick={onSave}
              disabled={!value.trim() || saving}
            >
              <FiCheck />
            </button>
            <button type="button" className="project-folder-cancel-btn" onClick={onCancel}>
              <FiX />
            </button>
          </div>
        </div>
        {error && <div className={`project-panel-error${span}`}>{error}</div>}
      </>
    );
  };

  const renderFileFolderCreateForm = (inGrid) => isCreatingFileFolder && renderFolderCreateForm({
    value: newFileFolderName,
    setValue: setNewFileFolderName,
    onSave: handleCreateFileFolder,
    onCancel: handleCancelCreateFileFolder,
    saving: creatingFileFolder,
    error: fileFolderError,
    inGrid,
  });

  const renderConversationFolderCreateForm = (inGrid) => isCreatingConversationFolder && renderFolderCreateForm({
    value: newConversationFolderName,
    setValue: setNewConversationFolderName,
    onSave: handleCreateConversationFolder,
    onCancel: handleCancelCreateConversationFolder,
    saving: creatingConversationFolder,
    error: conversationFolderError,
    inGrid,
  });

  const renderFileRow = (file) => {
    const isMoveMenuOpen = moveMenuFileId === file.file_id;
    const isEditingThisFile = editingFileId === file.file_id;
    // Editing: a plain checkbox against the DRAFT (nothing persisted yet).
    // Browsing: no checkbox at all -- the row itself is highlighted when the
    // SAVED scope includes it, the same "this one is active" language
    // .project-history-row.active already uses elsewhere in this panel.
    // Applied for the default "all files" scope too (every row highlighted)
    // at the user's explicit request, so the browse view always matches the
    // "N of M selected"/"All M selected" pill above it -- see
    // .claude/plan/plan_knowledge_scope.md.
    // Added by someone else after this conversation started: out of it
    // whatever the selection says, until "Use in this chat" adds it. Its
    // checkbox is off and locked -- the button is the one way in.
    const isHeld = heldFileIds.has(file.file_id);
    const isChecked = !isHeld && (scopeEditMode ? isFileInScope(file, draftScope) : isFileInScope(file, savedScope));
    // Only when the scope is actually narrowed (scopeIsRestricted) -- with
    // the default "all files" scope every row is in scope, so there's
    // nothing to visually exclude. Dims a not-in-scope row instead of
    // leaving it looking identical to an in-scope one with the highlight
    // simply not (yet) noticed -- see .project-file-row.scope-excluded.
    const isExcluded = !scopeEditMode && !isHeld && scopeIsRestricted && !isChecked;
    const isNew = !newFilesDismissed && newFileIds.has(file.file_id);
    const metaText = [fileAddedByLine(file), fileMetaLine(file)].filter(Boolean).join(' · ');
    return (
      <div
        key={file.file_id}
        className={`project-file-row${!scopeEditMode && isChecked ? ' scope-selected' : ''}${
          isExcluded ? ' scope-excluded' : ''
        }${isHeld ? ' scope-held' : ''}${isMoveMenuOpen ? ' move-menu-open' : ''}`}
        draggable={canEdit && fileFolders.length > 0 && !isEditingThisFile}
        onDragStart={(e) => handleFileDragStart(e, file.file_id)}
        onDragEnd={handleFileDragEnd}
      >
        {scopeEditMode && (
          <label className="project-file-scope-checkbox" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={isChecked}
              onChange={() => toggleFileDraft(file.file_id)}
              disabled={scopeSaving || isHeld}
              title={isHeld ? 'Added after this conversation started -- use "Use in this chat" to add it' : 'Searchable in this conversation'}
            />
          </label>
        )}
        <FileTypeGlyph file={file} projectId={project.project_id} />
        {isEditingThisFile ? (
          <div className="project-file-info project-folder-edit-form" onClick={(e) => e.stopPropagation()}>
            <input
              type="text"
              value={editingFileName}
              onChange={(e) => setEditingFileName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveEditFile(file.file_id);
                if (e.key === 'Escape') handleCancelEditFile();
              }}
              autoFocus
              className="project-folder-edit-input"
            />
            <div className="project-folder-edit-buttons">
              <button
                type="button"
                className="project-folder-save-btn"
                onClick={() => handleSaveEditFile(file.file_id)}
                disabled={!editingFileName.trim()}
              >
                <FiCheck />
              </button>
              <button type="button" className="project-folder-cancel-btn" onClick={handleCancelEditFile}>
                <FiX />
              </button>
            </div>
          </div>
        ) : (
          <div className="project-file-info">
            <div className="project-file-name" title={file.name}>{file.name}</div>
            {isHeld ? (
              // The same two lines as any other card: "Use in this chat" ends
              // the meta line, and the added-by text shortens to fit it.
              <div className="project-file-meta held">
                {isNew && <span className="project-file-new-tag">New</span>}
                {metaText && <span className="project-file-meta-text" title={metaText}>{metaText}</span>}
                <button
                  type="button"
                  className="project-file-held-use"
                  onClick={(e) => { e.stopPropagation(); handleUseHeldFile(file.file_id); }}
                  disabled={Boolean(heldAddingId)}
                  title="Added after this conversation started, so KAI isn't using it here yet. Click to let KAI use it in this conversation."
                >
                  {heldAddingId === file.file_id ? 'Adding…' : 'Use in this chat'}
                </button>
              </div>
            ) : (isNew || metaText) && (
              <div className="project-file-meta">
                {isNew && <span className="project-file-new-tag">New</span>}
                {metaText && <span className="project-file-meta-text" title={metaText}>{metaText}</span>}
              </div>
            )}
            {file.status === 'COMPLETED' && file.truncated && (
              <div className="project-panel-error">
                This document was too large to index in full — only its beginning is searchable.
              </div>
            )}
            {file.status === 'COMPLETED' && file.images_not_described > 0 && (
              <div className="project-panel-error">
                {file.images_not_described === 1
                  ? "1 image couldn't be described, so search can't find it by what it shows."
                  : `${file.images_not_described} images couldn't be described, so search can't find them by what they show.`}
                {' '}To retry, remove the file and upload it again.
              </div>
            )}
            {file.status === 'COMPLETED' && leftOutNotice(file) && (
              <div className="project-panel-error">{leftOutNotice(file)}</div>
            )}
          </div>
        )}
        {!isEditingThisFile && (
          // Preview is for everyone in the project, View included; moving,
          // renaming and removing need Edit.
          <div className="project-file-actions">
            <button
              type="button"
              className="project-file-remove"
              onClick={() => handleOpenFilePreview(file)}
              disabled={filePreviewLoadingName === file.name}
              title="Preview"
              aria-label={`Preview "${file.name}"`}
            >
              <FiEye size={14} />
            </button>
            {canEdit && fileFolders.length > 0 && (
              <div className="project-file-move-wrapper">
                <button
                  type="button"
                  className="project-file-remove"
                  onClick={() => toggleMoveMenuFor(file.file_id)}
                  title="Move to folder"
                  aria-label="Move to folder"
                >
                  <FiCornerUpRight size={14} />
                </button>
                {isMoveMenuOpen && (
                  <div className="project-file-move-menu" role="menu" onClick={(e) => e.stopPropagation()}>
                    {/* Same search + scroll UI as ConversationsSidebar.js's
                        own move-to-folder menu, at the user's explicit
                        request for consistency between the two. */}
                    <input
                      type="text"
                      className="project-file-move-search"
                      placeholder="Search folders"
                      value={moveMenuFolderSearchText}
                      onChange={(e) => setMoveMenuFolderSearchText(e.target.value)}
                      autoFocus
                    />
                    {getMoveMenuFolderMatches(fileFolders, file.folder_id).map((folder) => (
                      <button
                        key={folder.folder_id}
                        type="button"
                        role="menuitem"
                        className="project-file-move-menu-item"
                        onClick={() => handleMoveFileTo(file.file_id, folder.folder_id)}
                      >
                        <FiFolder size={13} /> {folder.name}
                      </button>
                    ))}
                    {getMoveMenuFolderMatches(fileFolders, file.folder_id).length === 0 && (
                      <div className="project-file-move-no-matches">No folders match</div>
                    )}
                    {file.folder_id && (
                      <button
                        type="button"
                        role="menuitem"
                        className="project-file-move-menu-item"
                        onClick={() => handleMoveFileTo(file.file_id, null)}
                      >
                        Files
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            {canEdit && (
              <>
                <button
                  type="button"
                  className="project-file-remove"
                  onClick={() => handleStartEditFile(file)}
                  title="Rename"
                  aria-label={`Rename "${file.name}"`}
                >
                  <FiEdit2 size={13} />
                </button>
                <button
                  type="button"
                  className="project-file-remove danger"
                  onClick={() => handleRemoveFile(file.file_id, file.name)}
                  disabled={removingId === file.file_id}
                  title="Remove from project"
                >
                  <FiTrash2 />
                </button>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderFileFolderGroup = (folder) => {
    const members = files.filter((f) => f.folder_id === folder.folder_id);
    const isCollapsed = !expandedFileFolderIds.has(folder.folder_id);
    const isDragTarget = dragOverFolderTarget === folder.folder_id;
    const isEditing = editingFileFolderId === folder.folder_id;
    const isChecked = scopeEditMode ? isFolderInScope(folder, draftScope) : isFolderInScope(folder, savedScope);
    return (
      <div
        key={folder.folder_id}
        className={`project-file-folder-group${isCollapsed ? '' : ' expanded'}`}
      >
        <div
          className={`project-file-folder-header${isDragTarget ? ' drag-over' : ''}${
            !scopeEditMode && isChecked ? ' scope-selected' : ''
          }${!scopeEditMode && scopeIsRestricted && !isChecked ? ' scope-excluded' : ''}`}
          onClick={isEditing ? undefined : () => toggleFileFolderCollapsed(folder.folder_id)}
          onDragOver={(e) => { e.stopPropagation(); handleFolderTargetDragOver(e, folder.folder_id); }}
          onDragLeave={() => handleFolderTargetDragLeave(folder.folder_id)}
          onDrop={(e) => { e.stopPropagation(); handleFolderTargetDrop(e, folder.folder_id); }}
        >
          {isEditing ? (
            <div className="project-folder-edit-form" onClick={(e) => e.stopPropagation()}>
              <input
                type="text"
                value={editingFileFolderName}
                onChange={(e) => setEditingFileFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveFileFolderEdit(folder.folder_id);
                  if (e.key === 'Escape') handleCancelEditFileFolder();
                }}
                autoFocus
                className="project-folder-edit-input"
              />
              <div className="project-folder-edit-buttons">
                <button
                  type="button"
                  className="project-folder-save-btn"
                  onClick={() => handleSaveFileFolderEdit(folder.folder_id)}
                  disabled={!editingFileFolderName.trim()}
                >
                  <FiCheck />
                </button>
                <button type="button" className="project-folder-cancel-btn" onClick={handleCancelEditFileFolder}>
                  <FiX />
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Checked = every file currently in this folder (including
                  ones added later -- a live reference, not a snapshot) is
                  searchable in this conversation. See renderFileRow's own
                  checkbox and .claude/plan/plan_knowledge_scope.md. */}
              {scopeEditMode && (
                <label className="project-file-scope-checkbox" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleFolderDraft(folder.folder_id)}
                    disabled={scopeSaving}
                    title="All files in this folder are searchable in this conversation"
                  />
                </label>
              )}
              {/* The toggle is a real <button> (sibling of the rename/delete
                  buttons) rather than a role="button" on the row itself --
                  nesting those inside a button-role container would hide
                  them from assistive tech. The row keeps its own onClick so
                  clicking anywhere still toggles for mouse users. */}
              <button
                type="button"
                className="project-file-folder-header-main"
                aria-expanded={!isCollapsed}
                onClick={(e) => { e.stopPropagation(); toggleFileFolderCollapsed(folder.folder_id); }}
              >
                {isCollapsed ? <FiChevronRight size={14} /> : <FiChevronDown size={14} />}
                <FiFolder size={14} className="project-file-folder-icon" />
                <span className="project-file-folder-name-wrap">
                  <span className="project-file-folder-name">{folder.name}</span>
                  <span className="project-file-count kai-count-badge">{members.length}</span>
                </span>
              </button>
              {canEdit && (
                <div className="project-file-folder-actions">
                  <button
                    type="button"
                    className="project-file-remove"
                    onClick={(e) => { e.stopPropagation(); handleStartEditFileFolder(folder); }}
                    title="Rename folder"
                  >
                    <FiEdit2 size={13} />
                  </button>
                  <button
                    type="button"
                    className="project-file-remove danger"
                    onClick={(e) => { e.stopPropagation(); handleDeleteFileFolder(folder); }}
                    title="Delete folder"
                  >
                    <FiTrash2 size={13} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        {!isCollapsed && (
          <div className="project-file-folder-members">
            {members.length === 0
              ? <div className="project-panel-empty project-file-folder-empty">{canEdit ? 'Drag a file here to file it.' : 'No files in this folder yet.'}</div>
              : members.map((file) => renderFileRow(file))}
          </div>
        )}
      </div>
    );
  };

  // A row is a <div>, not a <button>, because it holds its own rename/delete
  // buttons -- nesting a button inside another is invalid and browsers
  // resolve the click unpredictably. The name is the clickable target
  // instead, same split .project-file-row uses.
  const renderConversationRow = (conversation) => {
    const isEditingThis = editingConversationId === conversation.conversation_id;
    const isMoveMenuOpen = moveMenuConversationId === conversation.conversation_id;
    return (
      <div
        key={conversation.conversation_id}
        className={`project-history-row${
          conversation.conversation_id === activeConversationId ? ' active' : ''
        }${draggedOverConversationId === conversation.conversation_id ? ' drag-over' : ''}${
          isMoveMenuOpen ? ' move-menu-open' : ''
        }`}
        draggable={!isEditingThis}
        onDragStart={(e) => handleConversationDragStart(e, conversation.conversation_id)}
        onDragEnd={handleConversationDragEnd}
        onDragOver={(e) => handleConversationDragOver(e, conversation.conversation_id)}
        onDragLeave={() => handleConversationDragLeave(conversation.conversation_id)}
        onDrop={(e) => handleConversationDrop(e, conversation.conversation_id)}
      >
        {isEditingThis ? (
          <div className="project-folder-edit-form" onClick={(e) => e.stopPropagation()}>
            <input
              type="text"
              value={editingConversationName}
              onChange={(e) => setEditingConversationName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveEditConversation(conversation.conversation_id);
                if (e.key === 'Escape') handleCancelEditConversation();
              }}
              autoFocus
              className="project-folder-edit-input"
            />
            <div className="project-folder-edit-buttons">
              <button
                type="button"
                className="project-folder-save-btn"
                onClick={() => handleSaveEditConversation(conversation.conversation_id)}
                disabled={!editingConversationName.trim()}
              >
                <FiCheck />
              </button>
              <button type="button" className="project-folder-cancel-btn" onClick={handleCancelEditConversation}>
                <FiX />
              </button>
            </div>
          </div>
        ) : (
          <>
            <button
              type="button"
              className="project-history-open"
              onClick={() => onOpenConversation?.(conversation)}
              title={conversation.name}
            >
              <span className="project-history-title">
                <FocusModeTag focusMode={conversation.focus_mode} />
                <span className="project-history-name">{conversation.name}</span>
              </span>
              <span className="project-history-meta">{formatDate(conversation.updated_at)}</span>
            </button>
            <div className="project-file-actions">
              {conversationFolders.length > 0 && (
                <div className="project-file-move-wrapper">
                  <button
                    type="button"
                    className="project-file-remove"
                    onClick={() => toggleMoveMenuConversationFor(conversation.conversation_id)}
                    title="Move to folder"
                    aria-label="Move to folder"
                  >
                    <FiCornerUpRight size={14} />
                  </button>
                  {isMoveMenuOpen && (
                    <div className="project-file-move-menu" role="menu" onClick={(e) => e.stopPropagation()}>
                      {/* Same search + scroll UI as ConversationsSidebar.js's
                          own move-to-folder menu, at the user's explicit
                          request for consistency between the two. */}
                      <input
                        type="text"
                        className="project-file-move-search"
                        placeholder="Search folders"
                        value={moveMenuFolderSearchText}
                        onChange={(e) => setMoveMenuFolderSearchText(e.target.value)}
                        autoFocus
                      />
                      {getMoveMenuFolderMatches(conversationFolders, conversation.project_folder_id).map((folder) => (
                        <button
                          key={folder.folder_id}
                          type="button"
                          role="menuitem"
                          className="project-file-move-menu-item"
                          onClick={() => handleMoveConversationTo(conversation.conversation_id, folder.folder_id)}
                        >
                          <FiFolder size={13} /> {folder.name}
                        </button>
                      ))}
                      {getMoveMenuFolderMatches(conversationFolders, conversation.project_folder_id).length === 0 && (
                        <div className="project-file-move-no-matches">No folders match</div>
                      )}
                      {conversation.project_folder_id && (
                        <button
                          type="button"
                          role="menuitem"
                          className="project-file-move-menu-item"
                          onClick={() => handleMoveConversationTo(conversation.conversation_id, null)}
                        >
                          General conversations
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
              <button
                type="button"
                className="project-file-remove"
                // In a project nobody else is in yet, inviting comes first.
                onClick={() => (projectIsShared(project) ? setPromoteTarget(conversation) : setMembersModalOpen(true))}
                title={projectIsShared(project)
                  ? 'Make it a group chat with people in this project'
                  : 'Invite people first: group chats are shared with the people in this project'}
                aria-label={`Make "${conversation.name}" a group chat`}
              >
                <FiUsers size={13} />
              </button>
              <button
                type="button"
                className="project-file-remove"
                onClick={() => handleStartEditConversation(conversation)}
                title="Rename"
                aria-label={`Rename conversation "${conversation.name}"`}
              >
                <FiEdit2 size={13} />
              </button>
              <button
                type="button"
                className="project-file-remove danger"
                onClick={() => handleDeleteConversation(conversation)}
                disabled={deletingConversationId === conversation.conversation_id}
                title="Delete this conversation"
                aria-label={`Delete conversation "${conversation.name}"`}
              >
                <FiTrash2 />
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderConversationFolderGroup = (folder) => {
    // Preserves `history`'s current order -- see unfiledHistory's own note on
    // why this must not re-sort.
    const members = searchedHistory.filter((c) => c.project_folder_id === folder.folder_id);
    // While actively searching, a folder containing a match auto-expands
    // (same as ConversationsSidebar.js's own isCollapsed) so a hit isn't
    // hidden inside a collapsed folder the user never asked to open -- unless
    // the user has closed it during this search.
    const openForSearch = Boolean(historySearchQuery && members.length > 0);
    const isCollapsed = openForSearch
      ? searchClosedConversationFolderIds.has(folder.folder_id)
      : !expandedConversationFolderIds.has(folder.folder_id);
    const isDragTarget = dragOverConversationFolderTarget === folder.folder_id;
    const isEditing = editingConversationFolderId === folder.folder_id;
    return (
      <div
        key={folder.folder_id}
        className={`project-file-folder-group${isCollapsed ? '' : ' expanded'}`}
      >
        <div
          className={`project-file-folder-header${isDragTarget ? ' drag-over' : ''}`}
          onClick={isEditing ? undefined : () => toggleConversationFolderCollapsed(folder.folder_id, openForSearch)}
          onDragOver={(e) => { e.stopPropagation(); handleConversationFolderTargetDragOver(e, folder.folder_id); }}
          onDragLeave={() => handleConversationFolderTargetDragLeave(folder.folder_id)}
          onDrop={(e) => { e.stopPropagation(); handleConversationFolderTargetDrop(e, folder.folder_id); }}
        >
          {isEditing ? (
            <div className="project-folder-edit-form" onClick={(e) => e.stopPropagation()}>
              <input
                type="text"
                value={editingConversationFolderName}
                onChange={(e) => setEditingConversationFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveConversationFolderEdit(folder.folder_id);
                  if (e.key === 'Escape') handleCancelEditConversationFolder();
                }}
                autoFocus
                className="project-folder-edit-input"
              />
              <div className="project-folder-edit-buttons">
                <button
                  type="button"
                  className="project-folder-save-btn"
                  onClick={() => handleSaveConversationFolderEdit(folder.folder_id)}
                  disabled={!editingConversationFolderName.trim()}
                >
                  <FiCheck />
                </button>
                <button type="button" className="project-folder-cancel-btn" onClick={handleCancelEditConversationFolder}>
                  <FiX />
                </button>
              </div>
            </div>
          ) : (
            <>
              <button
                type="button"
                className="project-file-folder-header-main"
                aria-expanded={!isCollapsed}
                onClick={(e) => { e.stopPropagation(); toggleConversationFolderCollapsed(folder.folder_id, openForSearch); }}
              >
                {isCollapsed ? <FiChevronRight size={14} /> : <FiChevronDown size={14} />}
                <FiFolder size={14} className="project-file-folder-icon" />
                <span className="project-file-folder-name-wrap">
                  <span className="project-file-folder-name">{folder.name}</span>
                  <span className="project-file-count kai-count-badge">{members.length}</span>
                </span>
              </button>
              {/* Always the viewer's own folder (they're personal), so no role gate. */}
              <div className="project-file-folder-actions">
                <button
                  type="button"
                  className="project-file-remove"
                  onClick={(e) => { e.stopPropagation(); handleStartEditConversationFolder(folder); }}
                  title="Rename folder"
                >
                  <FiEdit2 size={13} />
                </button>
                <button
                  type="button"
                  className="project-file-remove danger"
                  onClick={(e) => { e.stopPropagation(); handleDeleteConversationFolder(folder); }}
                  title="Delete folder"
                >
                  <FiTrash2 size={13} />
                </button>
              </div>
            </>
          )}
        </div>
        {!isCollapsed && (
          <div className="project-file-folder-members">
            {members.length === 0
              ? <div className="project-panel-empty project-file-folder-empty">Drag a conversation here to file it.</div>
              : members.map((conversation) => renderConversationRow(conversation))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`project-panel project-panel-${variant}`}>
      <div className="project-panel-header">
        {headerRenaming ? (
          // Only the name itself is editable -- the "Project " label stays
          // plain text in front of it, same as ProjectSetupModal/App.js's
          // own inline-rename inputs never touch a fixed prefix either.
          <span className="project-panel-eyebrow project-panel-eyebrow-renaming">
            <FiLayers className="project-panel-eyebrow-icon" size={16} />
            <span className="project-panel-eyebrow-prefix">Project</span>
            <input
              type="text"
              className="project-panel-eyebrow-rename-input"
              value={headerRenameDraft}
              onChange={(e) => setHeaderRenameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCommitRenameHeader();
                if (e.key === 'Escape') handleCancelRenameHeader();
              }}
              autoFocus
              disabled={headerRenameBusy}
            />
            <button
              type="button"
              className="project-folder-save-btn"
              onClick={handleCommitRenameHeader}
              disabled={headerRenameBusy || !headerRenameDraft.trim()}
              title="Save"
              aria-label="Save project name"
            >
              <FiCheck />
            </button>
            <button
              type="button"
              className="project-folder-cancel-btn"
              onClick={handleCancelRenameHeader}
              disabled={headerRenameBusy}
              title="Cancel"
              aria-label="Cancel rename"
            >
              <FiX />
            </button>
          </span>
        ) : (
          // Plain, non-interactive label -- click "Rename" in the "..."
          // menu (below) to edit it right here instead.
          <span className="project-panel-eyebrow">
            <FiLayers className="project-panel-eyebrow-icon" size={16} />
            <span className="project-panel-eyebrow-name" title={project.name}>
              Project {project.name}
            </span>
          </span>
        )}
        {headerRenameError && <div className="project-panel-eyebrow-error">{headerRenameError}</div>}

        {/* "This project is shared" at a glance -- see
            .claude/plan/projectsharing.md. Shown to every viewer (creator
            and invited alike), clicking it opens the same members modal
            the "..." menu's "Manage members"/"See who has access" does. */}
        {members.length > 1 && (
          <button
            type="button"
            className="project-panel-avatar-stack"
            onClick={() => setMembersModalOpen(true)}
            aria-label={`People in this project: ${members.map(memberName).join('; ')}. See who has access`}
          >
            {/* Hovering a face names that person (a plain tooltip); the
                "+N" bubble names everyone it stands for. */}
            {members.slice(0, 3).map((m) => {
              const hasPhoto = typeof m.profile_img === 'string' && m.profile_img.trim()
                && m.profile_img.trim().toUpperCase() !== 'N/A';
              return hasPhoto ? (
                <img
                  key={m.user_id || m.email}
                  src={m.profile_img}
                  alt=""
                  title={memberName(m)}
                  referrerPolicy="no-referrer"
                  className="project-panel-avatar project-panel-avatar-img"
                />
              ) : (
                <span key={m.user_id || m.email} className="project-panel-avatar" title={memberName(m)}>
                  {(m.display_name || m.email || m.user_id || '?').trim().charAt(0).toUpperCase()}
                </span>
              );
            })}
            {members.length > 3 && (
              <span
                className="project-panel-avatar project-panel-avatar-more"
                title={members.slice(3).map(memberName).join(' · ')}
              >
                +{members.length - 3}
              </span>
            )}
          </button>
        )}

        {/* Rename / Move to Recycle Bin are Creator-only (administering the
            project itself) -- see .claude/plan/projectsharing.md's
            "Permission model". Manage members stays available to every
            viewer; the modal itself decides "Manage" vs "See who has
            access" wording. Pushed to the right edge of this header (see
            .project-panel-menu-wrapper's margin-left: auto). Used to be a
            "..." button next to the "Test" h1 title in ProjectView's own
            main column instead. */}
        {!headerRenaming && (
          <div className="project-panel-menu-wrapper" ref={projectMenuRef}>
            <button
              type="button"
              className="project-panel-menu-btn"
              onClick={() => setProjectMenuOpen((v) => !v)}
              title="Project options"
              aria-label="Project options"
            >
              <FiMoreHorizontal size={16} />
            </button>
            {projectMenuOpen && (
              <div className="project-panel-menu" role="menu">
                {isCreator && (
                  <button
                    type="button"
                    role="menuitem"
                    className="project-panel-menu-item"
                    onClick={() => { setProjectMenuOpen(false); handleStartRenameHeader(); }}
                  >
                    <FiEdit2 size={13} /> Rename
                  </button>
                )}
                <button
                  type="button"
                  role="menuitem"
                  className="project-panel-menu-item"
                  onClick={() => { setProjectMenuOpen(false); setMembersModalOpen(true); }}
                >
                  <FiUsers size={13} /> {canEdit ? 'Manage members' : 'See who has access'}
                </button>
                {!isCreator && (
                  <button
                    type="button"
                    role="menuitem"
                    className="project-panel-menu-item danger"
                    onClick={() => { setProjectMenuOpen(false); setLeaveModalOpen(true); }}
                  >
                    <FiLogOut size={13} /> Leave project
                  </button>
                )}
                {onDeleteProject && isCreator && (
                  <button
                    type="button"
                    role="menuitem"
                    className="project-panel-menu-item danger"
                    onClick={() => { setProjectMenuOpen(false); onDeleteProject(); }}
                  >
                    <FiTrash2 size={13} /> {project.shared_with > 0 ? 'Delete for everyone' : 'Move to Recycle Bin'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {onClose && (
          <button
            type="button"
            className="project-panel-close-btn"
            onClick={onClose}
            title="Close project panel"
            aria-label="Close project panel"
          >
            <FiX size={18} />
          </button>
        )}
      </div>

      <ProjectMembersModal
        isOpen={membersModalOpen}
        onClose={() => {
          setMembersModalOpen(false);
          // Someone may have just been invited, removed or given other access.
          loadMembers({ quiet: true });
          refreshProject();
        }}
        projectId={project.project_id}
        projectName={project.name}
        createdBy={project.created_by}
        myRole={project.my_role}
        ownerless={project.ownerless}
        onLeaveRequested={() => setLeaveModalOpen(true)}
      />

      <GroupChatPromoteModal
        isOpen={Boolean(promoteTarget)}
        conversation={promoteTarget}
        project={project}
        members={members}
        myUserId={user?.id}
        onClose={() => setPromoteTarget(null)}
        onPromoted={(conversation) => {
          const wasOpen = promoteTarget?.conversation_id === activeConversationId;
          setPromoteTarget(null);
          // It's left "My conversations" for the project's Group chats --
          // said out loud, so it doesn't look like it vanished.
          setGroupChatsNotice(`"${conversation.name}" is now a group chat. You'll find it here.`);
          setTimeout(() => setGroupChatsNotice(null), 6000);
          setGroupChatsRefreshKey((k) => k + 1);
          loadHistory({ quiet: true });
          if (wasOpen) onOpenConversation?.(conversation);
        }}
      />

      {/* Only ever opened for a non-creator (see the "..." menu and the members
          modal above). onLeft runs after the "you left" screen is dismissed and
          takes the user out of the project the same way a deleted one does. */}
      <LeaveProjectModal
        isOpen={leaveModalOpen}
        onClose={() => setLeaveModalOpen(false)}
        projectId={project.project_id}
        projectName={project.name}
        // Its owner already left: whoever leaves last takes it with them.
        isLastMember={Boolean(project.ownerless) && (
          members.length ? members.filter((m) => !m.pending).length : (project.shared_with || 0)
        ) <= 1}
        onLeft={() => onProjectDeleted?.(project.project_id)}
      />

      <div className="project-panel-tabs" role="tablist" aria-label="Project sections">
        {TABS.map((tab, index) => {
          const isActive = activeTab === tab.key;
          const Icon = tab.icon;
          const count = tabCount[tab.key];
          return (
            <button
              key={tab.key}
              ref={(el) => { tabRefs.current[index] = el; }}
              type="button"
              role="tab"
              id={`project-tab-${tab.key}`}
              aria-selected={isActive}
              aria-controls={`project-tabpanel-${tab.key}`}
              tabIndex={isActive ? 0 : -1}
              className={`project-panel-tab${isActive ? ' active' : ''}`}
              onClick={() => selectTab(tab.key)}
              onKeyDown={(e) => handleTabKeyDown(e, index)}
            >
              <span className="project-panel-tab-icon-wrap">
                <Icon className="project-panel-tab-icon" size={16} />
                {count > 0 && <span className="project-panel-tab-count kai-count-badge">{count}</span>}
              </span>
              <span className="project-panel-tab-label">
                <span className="project-panel-tab-label-text">{tab.label}</span>
                {tab.key === 'files' && newFiles.length > 0 && !newFilesTabOpened && (
                  <span className="project-panel-tab-new-dot" aria-label="New files" />
                )}
                {/* A new group chat, or someone else's message in one. Opening
                    the tab marks them seen (GroupChatsSection), which clears it. */}
                {tab.key === 'chats' && project?.group_unseen && !isActive && (
                  <span className="project-panel-tab-new-dot" aria-label="New in group chats" />
                )}
              </span>
            </button>
          );
        })}
      </div>

      <div className="project-panel-body">
        {activeTab === 'rules' && (
          <div
            className="project-panel-pane project-instructions-pane"
            role="tabpanel"
            id="project-tabpanel-rules"
            aria-labelledby="project-tab-rules"
            tabIndex={0}
          >
            <p className="project-instructions-hint">
              Applies to every conversation in this project.
            </p>
            <textarea
              className={`project-instructions-textarea${instructionsLocked ? ' locked' : ''}`}
              placeholder="e.g. Always cite page numbers. Use metric units. Write in a formal, professional tone."
              value={instructionsDraft}
              onChange={(e) => {
                setInstructionsDraft(e.target.value);
                if (instructionsError) setInstructionsError(null);
              }}
              onClick={() => { if (canEdit && instructionsLocked) setInstructionsLocked(false); }}
              // Clicking away from an untouched box locks it back; an edited
              // one stays open for Save / Cancel. Never saves by itself --
              // see handleInstructionsBlur.
              onBlur={canEdit ? handleInstructionsBlur : undefined}
              readOnly={!canEdit || instructionsLocked}
              disabled={instructionsSaving}
              title={!canEdit ? undefined : instructionsLocked ? 'Click to edit' : undefined}
            />
            <div className="project-instructions-footer">
              <div className="project-instructions-status">
                {instructionsError && (
                  <div className="project-panel-error">{instructionsError}</div>
                )}
                {!instructionsError && canEdit && !instructionsSaving
                  && instructionsDraft !== (project.instructions || '') && (
                  <div className="project-panel-status project-instructions-unsaved">
                    Unsaved changes. Save to apply them, or Cancel to discard.
                  </div>
                )}
                {!instructionsError && instructionsSavedAt
                  && instructionsDraft === (project.instructions || '') && (
                  <div className="project-panel-status">
                    Updated. Will apply to the whole project.
                  </div>
                )}
              </div>
              {/* View can read this project's instructions but never edit
                  them -- see .claude/plan/projectsharing.md's "Permission
                  model". */}
              {canEdit && (
                <div className="project-instructions-actions">
                  {instructionsDraft !== (project.instructions || '') && (
                    <button
                      type="button"
                      className="project-panel-pane-action"
                      // Keeps focus in the textarea, so clicking Cancel doesn't
                      // fire its onBlur first. Blur no longer saves (see
                      // handleInstructionsBlur), so this is just tidiness now.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={handleCancelInstructions}
                      disabled={instructionsSaving}
                    >
                      Cancel
                    </button>
                  )}
                  {/* Enabled while the box is actively
                      unlocked for editing, even with no changes typed yet
                      (clicking Save on an unchanged draft is still a valid
                      "yes, lock this back" action). Disabled once it's
                      locked again -- either by an actual save, or by the
                      textarea's own onBlur auto-locking it -- since at that
                      point there's nothing left open to save. */}
                  <button
                    type="button"
                    className="project-instructions-save-icon"
                    // Same as Cancel's onMouseDown above.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleSaveInstructions}
                    disabled={instructionsSaving || instructionsLocked}
                    title={instructionsSaving ? 'Saving…' : instructionsLocked ? 'Click into the box to edit' : 'Save instructions'}
                    aria-label="Save instructions"
                  >
                    <IoIosSave size={14} />
                    <span className="project-instructions-save-label">Save</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'files' && (
          <>
            <div
              className={`project-panel-pane${dragOver ? ' drag-over' : ''}${dragBlocked ? ' drag-blocked' : ''}`}
              role="tabpanel"
              id="project-tabpanel-files"
              aria-labelledby="project-tab-files"
              tabIndex={0}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {newFilesNotice && (
                <div className="project-files-new-notice" role="status">
                  <FiUsers className="project-files-new-notice-icon" size={14} aria-hidden="true" />
                  <div className="project-files-new-notice-text">
                    <div>
                      <strong>{newFilesNotice.who}</strong> added {newFilesNotice.what} to the knowledge base
                    </div>
                    {newFilesNotice.fileName && (
                      <div className="project-files-new-notice-file" title={newFilesNotice.fileName}>
                        {newFilesNotice.fileName}
                      </div>
                    )}
                    <div className="project-files-new-notice-sub">{newFilesNotice.usable}</div>
                  </div>
                  <button
                    type="button"
                    className="project-upload-dismiss"
                    onClick={() => setNewFilesDismissed(true)}
                    title="Dismiss"
                    aria-label="Dismiss"
                  >
                    <FiX size={13} />
                  </button>
                </div>
              )}

              {/* Explains what the blue left-accent (.scope-selected, see
                  renderFileRow/renderFileFolderGroup) actually means -- without
                  this, a highlighted row just looks like a selection state
                  with no stated purpose. */}
              {/* Takes the hint's own slot while a View member drags a file
                  in, so nothing under the cursor shifts mid-drag. */}
              {dragBlocked ? (
                <div className="project-panel-highlight-hint project-file-drop-blocked">
                  You have view access, so you can't add files to this project.
                </div>
              ) : !scopeEditMode && (
                <div className="project-panel-highlight-hint">
                  "Highlighted files are used as context in this conversation."
                </div>
              )}

              {/* The "X of Y selected"/"All Y selected" confirmation pill that
                  used to live here is gone -- the highlight-hint text above
                  already says what a highlighted row means, so restating the
                  count too was redundant right below it. The edit-mode
                  instruction and empty-library hint still earn their place
                  (there's no highlight to read yet in either case). */}
              {scopeEditMode ? (
                <div className="project-file-scope-hint">
                  Check the files and folders this conversation can search, then Save.
                </div>
              ) : files.length === 0 ? (
                <div className="project-file-scope-hint">
                  "Upload files to make them searchable in this conversation."
                </div>
              ) : null}
              {!scopeEditMode && scopeIsRestricted && tabCount.files === 0 && (
                <div className="project-file-scope-hint project-file-scope-hint-warning">
                  No files selected for this conversation -- KAI can't search any documents until you Select files.
                </div>
              )}
              {scopeSaveError && <div className="project-panel-error">{scopeSaveError}</div>}

              {/* Same "Folders" toolbar/heading (collapsible label + small
                  "+" to create one) as the Chats tab's own -- replaces this
                  tab's standalone "New folder" pane-action button. Only once
                  there is a folder. */}
              {fileFolders.length > 0 && (
              <div
                className={`folders-toolbar${fileFoldersSectionCollapsed ? ' collapsed' : ''}`}
                onClick={() => setFileFoldersSectionCollapsed((v) => !v)}
              >
                <button
                  type="button"
                  className="folders-toolbar-label"
                  aria-expanded={!fileFoldersSectionCollapsed}
                  onClick={(e) => {
                    e.stopPropagation();
                    setFileFoldersSectionCollapsed((v) => !v);
                  }}
                >
                  Folders
                  {fileFoldersSectionCollapsed && (
                    <span className="folders-toolbar-count">{fileFolders.length}</span>
                  )}
                </button>
                {canEdit && renderNewFolderButton(() => setIsCreatingFileFolder(true))}
              </div>
              )}
              {fileFolders.length > 0 && renderFileFolderCreateForm(false)}

              {/* One row per in-flight (or just-failed) upload -- see the
                  `uploads` state's own doc comment for why this is a list
                  rather than a single banner: multiple files, including a
                  slow video transcription, can be running at once. */}
              {uploads.map((upload) => (
                <div
                  key={upload.id}
                  className={`project-upload-progress${upload.status === 'failed' ? ' failed' : ''}`}
                >
                  {upload.status === 'failed' ? (
                    <>
                      <span className="project-upload-progress-text">{upload.name}: {upload.error}</span>
                      <button
                        type="button"
                        className="project-upload-dismiss"
                        onClick={() => removeUpload(upload.id)}
                        title="Dismiss"
                        aria-label={`Dismiss upload error for "${upload.name}"`}
                      >
                        <FiX size={13} />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="project-upload-spinner" style={{ animationDelay: `${upload.spinDelayMs}ms` }} />
                      {/* Always "Uploading…", even once the upload itself has
                          handed off to a background indexing job
                          (status === 'indexing' at that point) -- "indexing"
                          is an implementation detail the file list below
                          already surfaces per-file (see fileMetaLine) once
                          this row is gone. */}
                      <span className="project-upload-progress-text">
                        {upload.isMedia
                          ? `Transcribing "${upload.name}"… (this can take a few minutes)`
                          : `Uploading "${upload.name}"…`}
                      </span>
                    </>
                  )}
                </div>
              ))}
              {uploadNote && <div className="project-upload-note">{uploadNote}</div>}

              {filesLoading && <SkeletonRows count={3} rowHeight={68} />}

              {!filesLoading && filesError && (
                <div className="project-panel-error">{filesError}</div>
              )}

              {/* Two columns once this panel (not the viewport -- see the
                  container query in ProjectPanel.css) is wide enough for a
                  card to stay a comfortable width, one column otherwise --
                  covers both "this is the narrow sidebar variant" and "this
                  is the full-page project view on a small window" the same
                  way, since both are really just "how wide is this
                  container." An EXPANDED folder (.expanded, see
                  renderFileFolderGroup) spans both columns instead of
                  pairing with a short sibling and leaving it stranded next
                  to a much taller card. */}
              <div className="project-panel-grid">
                {!filesLoading && !fileFoldersSectionCollapsed
                  && sortedFileFolders.map((folder) => renderFileFolderGroup(folder))}

                {/* Shown even with no folders, where it also holds the
                    button to make the first one -- for an editor, even before
                    there are any files. */}
                {!filesLoading && !filesError && (unfiledFiles.length > 0 || (canEdit && fileFolders.length === 0)) && (
                  <div
                    className={`project-file-unfiled-label project-panel-grid-span${
                      canEdit && fileFolders.length === 0 ? ' project-file-unfiled-label--with-action' : ''
                    }`}
                  >
                    Files
                    {canEdit && fileFolders.length === 0 && renderNewFolderButton(() => setIsCreatingFileFolder(true))}
                  </div>
                )}
                {fileFolders.length === 0 && renderFileFolderCreateForm(true)}
                {!filesLoading && !filesError && files.length === 0 && fileFolders.length === 0 && uploads.length === 0 && (
                  <div className="project-panel-empty project-panel-grid-span">
                    {canEdit
                      ? 'No files yet. Drop a PDF or drawing here and every answer in this project can use it.'
                      : 'No files yet. Files added by the project\'s editors will appear here.'}
                  </div>
                )}
                {!filesLoading && sortedUnfiledFiles.map((file) => renderFileRow(file))}
                {/* Even with nothing currently unfiled, a project with folders
                    still needs a place to drop a file BACK OUT of one -- without
                    this, "drag it out of the folder" would have nowhere to land. */}
                {!filesLoading && fileFolders.length > 0 && unfiledFiles.length === 0 && (
                  <div
                    className={`project-file-unfiled-dropzone project-panel-grid-span${dragOverFolderTarget === 'unfiled' ? ' drag-over' : ''}`}
                    onDragOver={(e) => handleFolderTargetDragOver(e, 'unfiled')}
                    onDragLeave={() => handleFolderTargetDragLeave('unfiled')}
                    onDrop={(e) => handleFolderTargetDrop(e, 'unfiled')}
                  >
                    Drag a file here to take it out of its folder
                  </div>
                )}
              </div>
            </div>

            {/* Pinned below the scroll area, not inside it, so "upload a
                file" is always on screen -- never requires scrolling past
                a long file list first. One row: project/library management
                (Upload files) and the Files tab's own selection step
                (Select files, or -- once editing -- Select all/Clear/
                Cancel/Save) live side by side rather than stacked, so this
                stays a single control bar, not two. See
                .claude/plan/plan_knowledge_scope.md. */}
            <div
              className={`project-panel-pane-actions project-panel-pane-actions-footer${
                scopeEditMode ? ' scope-editing' : ''
              }`}
            >
              {!scopeEditMode ? (
                <>
                  {canEdit && (
                    <>
                      <button
                        type="button"
                        className="project-panel-pane-action equal primary"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <FiUpload size={13} /> Upload files
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept={ALL_UPLOAD_EXTENSIONS.join(',')}
                        className="project-upload-input"
                        onChange={handleFileInputChange}
                      />
                    </>
                  )}
                  {/* An explicit step, not a silently-always-editable list:
                      outside edit mode every row/folder just shows which of
                      them are CURRENTLY selected (a highlight, see
                      renderFileRow), with no checkboxes in sight. This is
                      what turns them on. */}
                  <button
                    type="button"
                    className="project-panel-pane-action equal"
                    onClick={handleEnterScopeEditMode}
                  >
                    <FiCheckSquare size={13} /> Select files
                  </button>
                </>
              ) : (
                <>
                  {/* Nothing is applied until "Save" -- "Cancel" walks away
                      from the draft untouched. */}
                  <button
                    type="button"
                    className="project-panel-pane-action"
                    onClick={handleDraftSelectAll}
                    disabled={scopeSaving}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    className="project-panel-pane-action"
                    onClick={handleDraftClear}
                    disabled={scopeSaving}
                  >
                    Clear
                  </button>
                  <div className="project-file-scope-toolbar-spacer" />
                  <button
                    type="button"
                    className="project-panel-pane-action"
                    onClick={handleCancelScopeEdit}
                    disabled={scopeSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="project-panel-pane-action primary"
                    onClick={handleSaveScopeEdit}
                    disabled={scopeSaving}
                  >
                    {scopeSaving ? 'Saving…' : 'Save'}
                  </button>
                </>
              )}
            </div>
          </>
        )}

        {activeTab === 'chats' && (
          <>
            {/* Same "Filter conversations" box ConversationsSidebar.js has
                for the regular history list -- at the user's explicit
                request for consistency between the two. */}
            <div className="project-panel-search">
              <div className="filter-input-wrapper">
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Search conversations"
                  value={historySearchText}
                  onChange={(e) => setHistorySearchText(e.target.value)}
                />
                {historySearchText && (
                  <button
                    type="button"
                    className="filter-clear-btn"
                    onClick={() => setHistorySearchText('')}
                    title="Clear search"
                    aria-label="Clear search"
                  >
                    <FiX size={14} />
                  </button>
                )}
              </div>
            </div>

            <div
              className="project-panel-pane"
              role="tabpanel"
              id="project-tabpanel-chats"
              aria-labelledby="project-tab-chats"
              tabIndex={0}
            >
              <GroupChatsSection
                project={project}
                activeConversationId={activeConversationId}
                onOpenConversation={onOpenConversation}
                onInviteRequested={() => setMembersModalOpen(true)}
                refreshKey={groupChatsRefreshKey}
                activityKey={conversationActivityTrigger}
                notice={groupChatsNotice}
                members={members}
                myUserId={user?.id}
                onChatDeleted={(conversationId) => {
                  setLeftChats((prev) => prev.filter((c) => c.conversation_id !== conversationId));
                  onConversationDeleted?.(conversationId);
                }}
                leftChats={leftChats}
                searchQuery={historySearchQuery}
                onChatLeft={() => loadHistory({ quiet: true })}
                onChatsCounted={setGroupChatCount}
                onGroupChatsSeen={(seenProjectId) => {
                  // As for unseen above: App's copy keeps its dot until told.
                  const { project: current, onProjectUpdated: notify } = latestRef.current;
                  if (current?.project_id === seenProjectId) notify?.({ ...current, group_unseen: false });
                }}
              />
              {/* Same "Folders" toolbar/heading (collapsible label + small
                  "+" to create one) ConversationsSidebar.js has above its
                  own folder list -- replaces this tab's standalone "New
                  folder" pane-action button. Only once there is a folder. */}
              {conversationFolders.length > 0 && (
              <div
                className={`folders-toolbar${conversationFoldersSectionCollapsed ? ' collapsed' : ''}`}
                onClick={() => setConversationFoldersSectionCollapsed((v) => !v)}
              >
                <button
                  type="button"
                  className="folders-toolbar-label"
                  aria-expanded={!conversationFoldersSectionCollapsed}
                  onClick={(e) => {
                    e.stopPropagation();
                    setConversationFoldersSectionCollapsed((v) => !v);
                  }}
                >
                  Folders
                  {conversationFoldersSectionCollapsed && (
                    <span className="folders-toolbar-count">{conversationFolders.length}</span>
                  )}
                </button>
                {/* Conversation folders are personal, like the conversations
                    in them -- every member manages their own, whatever their
                    role (see project_routes.py's conversation-folder routes). */}
                {renderNewFolderButton(() => setIsCreatingConversationFolder(true))}
              </div>
              )}
              {conversationFolders.length > 0 && renderConversationFolderCreateForm(false)}

              {historyLoading && <SkeletonRows count={3} />}
              {!historyLoading && historyError && (
                <div className="project-panel-error">{historyError}</div>
              )}
              {reorderError && <div className="project-panel-error">{reorderError}</div>}

              <div className="project-panel-grid">
                {!historyLoading && !conversationFoldersSectionCollapsed
                  && visibleConversationFolders.map((folder) => renderConversationFolderGroup(folder))}

                {/* Shown even with no folders, where it also holds the
                    button to make the first one -- even before there are any
                    conversations, or when a search matches none. */}
                {!historyLoading && !historyError && (unfiledHistory.length > 0 || conversationFolders.length === 0) && (
                  <div
                    className={`project-file-unfiled-label project-file-unfiled-label--toggle project-panel-grid-span${
                      conversationFolders.length === 0 ? ' project-file-unfiled-label--with-action' : ''
                    }`}
                    onClick={() => setGeneralConversationsCollapsed((v) => !v)}
                  >
                    <button
                      type="button"
                      className="project-file-unfiled-toggle"
                      aria-expanded={!generalConversationsCollapsed}
                      onClick={(e) => {
                        e.stopPropagation();
                        setGeneralConversationsCollapsed((v) => !v);
                      }}
                    >
                      General conversations
                      {generalConversationsCollapsed && unfiledHistory.length > 0 && (
                        <span className="folders-toolbar-count">{unfiledHistory.length}</span>
                      )}
                    </button>
                    {conversationFolders.length === 0
                      && renderNewFolderButton(() => setIsCreatingConversationFolder(true))}
                  </div>
                )}
                {conversationFolders.length === 0 && renderConversationFolderCreateForm(true)}
                {!historyLoading && !historyError && !generalConversationsCollapsed
                  && history.length === 0 && conversationFolders.length === 0 && (
                  <div className="project-panel-empty project-panel-grid-span">
                    No conversations yet. Start one below and it'll be saved here, out of your main history.
                  </div>
                )}
                {!historyLoading && !historyError && historySearchQuery
                  && (history.length > 0 || conversationFolders.length > 0)
                  && unfiledHistory.length === 0 && visibleConversationFolders.length === 0 && (
                  <div className="project-panel-empty project-panel-grid-span">
                    No conversations match your search.
                  </div>
                )}
                {!historyLoading && !generalConversationsCollapsed
                  && unfiledHistory.map((conversation) => renderConversationRow(conversation))}
                {/* Even with nothing currently unfiled, a project with folders
                    still needs a place to drop a conversation BACK OUT of one --
                    without this, "drag it out of the folder" would have nowhere
                    to land. */}
                {!historyLoading && conversationFolders.length > 0 && unfiledHistory.length === 0 && (
                  <div
                    className={`project-file-unfiled-dropzone project-panel-grid-span${dragOverConversationFolderTarget === 'unfiled' ? ' drag-over' : ''}`}
                    onDragOver={(e) => handleConversationFolderTargetDragOver(e, 'unfiled')}
                    onDragLeave={() => handleConversationFolderTargetDragLeave('unfiled')}
                    onDrop={(e) => handleConversationFolderTargetDrop(e, 'unfiled')}
                  >
                    Drag a conversation here to take it out of its folder
                  </div>
                )}
              </div>
            </div>

            {/* Same bottom-pinned bar the Knowledge base tab uses (see its
                own comment above) -- "New conversation" stays visible no
                matter how far the chats/folders list scrolls, instead of
                requiring a scroll back to the top first. */}
            <div className="project-panel-pane-actions project-panel-pane-actions-footer">
              <button
                type="button"
                className="project-panel-pane-action equal primary"
                onClick={() => onStartConversationInProject?.(project)}
              >
                <FiPlusSquare size={13} /> New conversation
              </button>
            </div>
          </>
        )}
      </div>

      {confirmModal && (
        <div className="project-panel-modal-overlay" onClick={closeConfirmModal} role="presentation">
          <div
            className="project-panel-modal"
            role="dialog"
            aria-labelledby="project-panel-confirm-title"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 id="project-panel-confirm-title">{confirmModal.title}</h4>
            {confirmModal.subject && (
              <div className="project-panel-modal-subject" title={confirmModal.subject}>
                {confirmModal.subjectIcon}
                <span>{confirmModal.subject}</span>
              </div>
            )}
            <p>{confirmModal.message}</p>
            <div className="project-panel-modal-actions">
              <button
                type="button"
                className="project-panel-modal-cancel"
                onClick={closeConfirmModal}
                disabled={!!confirmBusy}
              >
                Cancel
              </button>
              {confirmModal.secondaryLabel && (
                <button
                  type="button"
                  className="project-panel-modal-secondary"
                  onClick={runSecondaryConfirmModal}
                  disabled={!!confirmBusy}
                >
                  {confirmBusy === 'secondary' ? confirmModal.secondaryBusyLabel : confirmModal.secondaryLabel}
                </button>
              )}
              <button
                type="button"
                className={`project-panel-modal-confirm${confirmModal.confirmVariant === 'primary' ? ' variant-primary' : ''}`}
                onClick={runConfirmModal}
                disabled={!!confirmBusy}
              >
                {confirmBusy === 'primary' ? confirmModal.busyLabel : confirmModal.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {filePreviewError && (
        <div className="project-panel-modal-overlay" onClick={() => setFilePreviewError(null)} role="presentation">
          <div
            className="project-panel-modal"
            role="dialog"
            aria-labelledby="project-file-preview-error-title"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 id="project-file-preview-error-title">Can't preview this file</h4>
            <p>{filePreviewError}</p>
            <div className="project-panel-modal-actions">
              <button
                type="button"
                className="project-panel-modal-confirm variant-primary"
                onClick={() => setFilePreviewError(null)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {filePreview && (
        <div
          className="project-file-preview-overlay"
          role="presentation"
          onClick={() => setFilePreview(null)}
        >
          <div
            className="project-file-preview-modal"
            role="dialog"
            aria-labelledby="project-file-preview-title"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="project-file-preview-header">
              <span id="project-file-preview-title" className="project-file-preview-title">
                {filePreview.name}
              </span>
              <div className="project-file-preview-header-actions">
                <a
                  href={filePreview.download_url}
                  className="project-file-preview-action"
                  title="Download"
                  aria-label="Download"
                >
                  <FiDownload size={14} /> Download
                </a>
                {previewNewTabUrl(filePreview) && (
                  <a
                    href={previewNewTabUrl(filePreview)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="project-file-preview-action"
                    title="Open in a new tab"
                    aria-label="Open in a new tab"
                  >
                    Open in new tab
                  </a>
                )}
                <button
                  type="button"
                  className="project-file-preview-close"
                  onClick={() => setFilePreview(null)}
                  title="Close preview"
                  aria-label="Close preview"
                >
                  <FiX size={16} />
                </button>
              </div>
            </div>
            <FilePreviewBody preview={filePreview} />
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectPanel;
